/**
 * DecisionFlow — PlanDetailView
 * ui/src/components/tenant-admin/PlanDetailView.jsx
 *
 * Spec authority: comp_policies_spec_s539.md §7, §7A, §9 | mastercontext_s541.md Step 4A
 * Session: S545 (S541 base + §9 Save as Template wizard)
 *
 * Routes:
 *   /admin/plans/:planId
 *   /admin/plans/:planId/components/:componentId/:modifier
 *
 * Layout:
 *   1. Header — plan name, code, segment/role/BU pills, version selector, status, actions
 *   2. Amendment banner — persistent amber, sticky, non-dismissible while open (§7A)
 *   3. Currency sub-header — OTE currencies across components
 *   4. Component pill strip — horizontal, overflow → +N More
 *   5. Modifier card grid — 8 cards per component, 5-state + FX overlay
 *   6. Embedded manager region — when modifier card opened
 *
 * S545 additions (§9 — Save as Template):
 *   Button in header action row:  [Create Change]  [Save as Template]
 *
 *   Availability (§9):
 *     plan_status === 'approved' AND !hasOpenAmendment → visible + enabled
 *     Other statuses → hidden
 *     Amendment open → visible but disabled with tooltip
 *
 *   Four-step guided wizard (modal) → POST /api/v1/template-library/templates/from-plan/{plan_id}
 *     Step 1 — Review Source: source plan name, version, approval status, component count (immutable)
 *     Step 2 — Template Metadata: auto-populated name + code, editable description + tags
 *     Step 3 — Review Generated Package: component count, validation warnings, origin
 *     Step 4 — Create Draft: summary + governance notice + "Create Draft Template" button
 *
 *   Post-create: toast "Template draft created. Submit for approval in the Template Library." + link
 *
 *   Governance notice (always visible on Step 4 — §9 Critical Governance Rule):
 *     "This plan's approval is not transferred to the template. The new template draft
 *      requires separate two-actor approval before it can be deployed."
 *
 *   No approval inheritance. No AI. No mock data.
 *   NN-36: Two-actor approval on template draft required separately.
 *
 * POST body: TemplateFromPlanRequest { template_name?, template_code?, description?, tags? }
 * Response (201): { template_id, template_version_id, template_name, ... }
 *
 * API (all from S541 — unchanged):
 *   GET  /api/v1/plans/{plan_id}
 *   GET  /api/v1/plan-components   (filtered client-side by plan_id)
 *   GET  /api/v1/roles
 *   GET  /api/v1/segments
 *   GET  /api/v1/business-units
 *   GET  /api/v1/exchange-rates?is_locked=false&fiscal_period_id={id}
 *   GET  /api/v1/kicker-plans?plan_component_id={id}
 *   GET  /api/v1/clawback-policies?plan_component_id={id}
 *   GET  /api/v1/revenue-type-rate-tables?plan_component_id={id}
 *   GET  /api/v1/draw-plans?plan_component_id={id}
 *   GET  /api/v1/split-plans?plan_component_id={id}
 *   GET  /api/v1/spiff-plans?plan_component_id={id}
 *   GET  /api/v1/transaction-plans?plan_component_id={id}
 *   GET  /api/v1/amendment-stage-impact-map
 *   GET  /api/v1/amendments
 *   POST /api/v1/amendments/{id}/commit
 *   POST /api/v1/amendments/{id}/cancel
 *   POST /api/v1/template-library/templates/{id}/versions
 *   POST /api/v1/template-library/templates/from-plan/{plan_id}  ← S545 NEW
 *
 * Props:
 *   mode   — 'wizard' | 'admin'
 *   period — { fiscal_period_id, ... } | null
 *
 * CSS: index.css tokens — rgb(var(--color-*)), skeleton, IBM Plex Sans
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext.jsx';
import { apiGet, apiPatch, apiPost } from '../../api/client';

import { GitBranch, Clock, CheckCircle } from 'lucide-react';
import AcceleratorCapManager    from './AcceleratorCapManager.jsx';
import KickerPlanManager        from './KickerPlanManager.jsx';
import RevTypeRateTableManager  from './RevTypeRateTableManager.jsx';
import ClawbackPolicyManager    from './ClawbackPolicyManager.jsx';
import DrawPlanManager          from './DrawPlanManager.jsx';
import DealSplitManager         from './DealSplitManager.jsx';
import SpiffPlanManager         from './SpiffPlanManager.jsx';
import TransactionPlanManager   from './TransactionPlanManager.jsx';
// Phase 4 — Pattern A modifier managers (S377)
import MarginQuotaManager       from './MarginQuotaManager.jsx';
import RetentionQuotaManager    from './RetentionQuotaManager.jsx';
import VolumeManager            from './VolumeManager.jsx';
import MultiYearManager         from './MultiYearManager.jsx';
import RankingManager           from './RankingManager.jsx';
// S689 — 12 strategic managers wired (formerly missing from MANAGER_MAP / coming_soon)
import TerritoryManager         from './TerritoryManager.jsx';
import OverlayAttributionManager from './OverlayAttributionManager.jsx';
import CoSellManager            from './CoSellManager.jsx';
import TrueUpManager            from './TrueUpManager.jsx';
import PodManager               from './PodManager.jsx';
import ContestManager           from './ContestManager.jsx';
import BonusPoolManager         from './BonusPoolManager.jsx';
import NonCashAwardManager      from './NonCashAwardManager.jsx';
import MarketShareManager       from './MarketShareManager.jsx';
import PharmaManager            from './PharmaManager.jsx';
import ChannelManager           from './ChannelManager.jsx';
import PlanVersioningManager    from './PlanVersioningManager.jsx';
// S689 — PlanTypeSelector for custom path empty state (Change 4 / PATH B)
import PlanTypeSelector         from './PlanTypeSelector.jsx';
// Amendment components — wire only, do not rebuild (§7A / UI-02)
import AmendmentBeginModal      from '../admin/AmendmentBeginModal.jsx';
import AmendmentDeltaPanel      from '../admin/AmendmentDeltaPanel.jsx';
// §10 Blueprint — S549
import CompensationPackageBlueprint from '../admin/CompensationPackageBlueprint.jsx';
// S611 §8 — Modifier grid extraction
import PlanTypeModifierGrid from '../admin/PlanTypeModifierGrid.jsx';
// S690 — OI-PATH-B-UX-SIMPLIFICATION: type-aware accordion checklist
// Replaces PlanTypeModifierGrid when applicabilityRows are loaded for a component.
import PlanModifierChecklist from './PlanModifierChecklist.jsx';
// S693 — PlanWizardShell: presentational wrapper for wizard step flow
import PlanWizardShell from './PlanWizardShell.jsx';
// S694 — TemplateDiffView: Template Difference Engine (Bucket 3 / AD-68)
import TemplateDiffView from './TemplateDiffView.jsx';
// S697 — RateTableManager: ARR attainment earnings curve (GAP-PDV-05 / S28)
import RateTableManager from './RateTableManager.jsx';
// S737 Step 4 — CHANGE-12: PlanConfigSummary — primary view for approved/pending plans (AD-198)
import PlanConfigSummary from './PlanConfigSummary.jsx';


// ---------------------------------------------------------------------------
// Constants — 8 modifier cards per component (spec §4.7)
// ---------------------------------------------------------------------------

const MODIFIER_CARDS = [
  { key: 'transaction', label: 'Transaction Plans',  path: 'transaction', endpoint: '/api/v1/transaction-plans',         hasFx: false, tier: 'standard'   },
  { key: 'draw',        label: 'Draw Plans',          path: 'draw',        endpoint: '/api/v1/draw-plans',                hasFx: true,  tier: 'standard'   },
  { key: 'deal-split',  label: 'Deal Splits',         path: 'deal-split',  endpoint: '/api/v1/split-plans',               hasFx: false, tier: 'standard'   },
  { key: 'spiff',       label: 'SPIFF Plans',         path: 'spiff',       endpoint: '/api/v1/spiff-plans',               hasFx: true,  tier: 'standard'   },
  { key: 'rev-rates',   label: 'Revenue Type Rates',  path: 'rev-rates',   endpoint: '/api/v1/revenue-type-rate-tables',  hasFx: false, tier: 'standard'   },
  { key: 'kicker',      label: 'Kicker / Multiplier', path: 'kicker',      endpoint: '/api/v1/kicker-plans',              hasFx: false, tier: 'standard'   },
  { key: 'caps',        label: 'Accelerator Caps',    path: 'caps',        endpoint: '/api/v1/plan-components',           hasFx: true,  tier: 'standard'   },
  { key: 'clawback',    label: 'Clawback Policies',   path: 'clawback',    endpoint: '/api/v1/clawback-policies',         hasFx: false, tier: 'standard'   },
  // Phase 4 — Pattern A modifier cards (S377)
  { key: 'margin',      label: 'Margin Quota',        path: 'margin',      endpoint: '/api/v1/margin-quotas',             hasFx: true,  tier: 'enterprise' },
  { key: 'retention',   label: 'Retention Quota',     path: 'retention',   endpoint: '/api/v1/retention-quotas',          hasFx: false, tier: 'standard'   },
  { key: 'volume',      label: 'Volume / Unit',       path: 'volume',      endpoint: '/api/v1/unit-quotas',               hasFx: false, tier: 'standard'   },
  { key: 'multi-year',  label: 'Multi-Year Deals',    path: 'multi-year',  endpoint: '/api/v1/multi-year-policies',  hasFx: false, tier: 'enterprise' },
  { key: 'ranking',     label: 'Ranking Plans',       path: 'ranking',     endpoint: '/api/v1/ranking-plans',             hasFx: false, tier: 'enterprise' },
  // S689 — 12 strategic managers wired (Change 6 / AD-60)
  { key: 'territory',   label: 'Territory Compensation', path: 'territory',  endpoint: '/api/v1/territory-assignments',    hasFx: false, tier: 'standard'   },
  { key: 'overlay',     label: 'Overlay Attribution',    path: 'overlay',    endpoint: '/api/v1/overlay-attributions',    hasFx: false, tier: 'standard'   },
  { key: 'cosell',      label: 'Co-Sell / Channel',      path: 'cosell',     endpoint: '/api/v1/cosell-frameworks',       hasFx: false, tier: 'standard'   },
  { key: 'trueup',      label: 'Retroactive True-Up',    path: 'trueup',     endpoint: '/api/v1/true-up-policies',        hasFx: false, tier: 'standard'   },
  { key: 'pod',         label: 'Pod / Team',             path: 'pod',        endpoint: '/api/v1/pod-compensations',       hasFx: false, tier: 'standard'   },
  { key: 'contest',     label: 'Contest Programs',       path: 'contest',    endpoint: '/api/v1/contest-programs',        hasFx: false, tier: 'standard'   },
  { key: 'bonuspool',   label: 'Bonus Pool',             path: 'bonuspool',  endpoint: '/api/v1/bonus-pools',             hasFx: false, tier: 'standard'   },
  { key: 'noncash',     label: 'Non-Cash Awards',        path: 'noncash',    endpoint: '/api/v1/non-cash-awards',         hasFx: false, tier: 'standard'   },
  { key: 'marketshare', label: 'Market Share',           path: 'marketshare',endpoint: '/api/v1/market-share-plans',     hasFx: false, tier: 'standard'   },
  { key: 'pharma',      label: 'Pharma Script',          path: 'pharma',     endpoint: '/api/v1/pharma-plans',            hasFx: false, tier: 'standard'   },
  { key: 'channel',     label: 'Channel / Partner',      path: 'channel',    endpoint: '/api/v1/channel-compensations',   hasFx: false, tier: 'standard'   },
  { key: 'versioning',  label: 'Plan Versioning',        path: 'versioning', endpoint: '/api/v1/plan-versions',           hasFx: false, tier: 'standard'   },
];

const MANAGER_MAP = {
  'transaction': TransactionPlanManager,
  'draw':        DrawPlanManager,
  'deal-split':  DealSplitManager,
  'spiff':       SpiffPlanManager,
  'rev-rates':   RevTypeRateTableManager,
  'kicker':      KickerPlanManager,
  'caps':        AcceleratorCapManager,
  'clawback':    ClawbackPolicyManager,
  'margin':      MarginQuotaManager,
  'retention':   RetentionQuotaManager,
  'volume':      VolumeManager,
  'multi-year':  MultiYearManager,
  'ranking':     RankingManager,
  // S689 — 12 strategic managers wired (Change 6)
  'territory':   TerritoryManager,
  'overlay':     OverlayAttributionManager,
  'cosell':      CoSellManager,
  'trueup':      TrueUpManager,
  'pod':         PodManager,
  'contest':     ContestManager,
  'bonuspool':   BonusPoolManager,
  'noncash':     NonCashAwardManager,
  'marketshare': MarketShareManager,
  'pharma':      PharmaManager,
  'channel':     ChannelManager,
  'versioning':  PlanVersioningManager,
};

// ---------------------------------------------------------------------------
// AD-45 / AD-60 — Grid key translation (S684 + S689 complete)
// PlanTypeModifierGrid CARD_DEFS keys → PlanDetailView MODIFIER_CARDS keys.
// Translation lives here only — PlanTypeModifierGrid keys are never changed.
// S689: All 29 plan type keys mapped. Missing entries caused silent no-ops (AD-60).
// Performance cards not listed here are either weight cards (handled by WEIGHT_CARD_KEYS)
// or have matching keys in MODIFIER_CARDS already.
// ---------------------------------------------------------------------------

const GRID_KEY_TO_MODIFIER = {
  // Performance (P3B)
  'accelerator_caps':      'caps',
  'kicker_rules':          'kicker',
  'revenue_type_rules':    'rev-rates',
  'churn_clawback':        'clawback',
  // Structural (P3B)
  'draw':                  'draw',
  'deal-split':            'deal-split',
  'spiff':                 'spiff',
  'transaction':           'transaction',
  // Strategic (P4)
  'territory_compensation': 'territory',
  'overlay_attribution':    'overlay',
  'cosell_framework':       'cosell',
  'true_up_policy':         'trueup',
  'pod_compensation':       'pod',
  'contest_programs':       'contest',
  'bonus_pool':             'bonuspool',
  'non_cash_awards':        'noncash',
  'market_share':           'marketshare',
  'pharma_script':          'pharma',
  'channel_compensation':   'channel',
  'plan_versioning':        'versioning',
  // P4 Pattern A (already in MODIFIER_CARDS under matching keys)
  'margin_compensation':    'margin',
  'grr_nrr_retention':      'retention',
  'volume_compensation':    'volume',
  'multi_year_deals':       'multi-year',
  'ranking_plans':          'ranking',
};

// AD-44 — Weight card keys open PlanBuilder inline, not a modifier manager
const WEIGHT_CARD_KEYS = new Set([
  'core_ic_plan', 'mbo_components', 'nps_components', 'adoption_components',
]);

const WEIGHT_CARD_FOCUS_MAP = {
  'core_ic_plan':        'arr_weight',
  'mbo_components':      'mbo_weight',
  'nps_components':      'nps_weight',
  'adoption_components': 'adoption_weight',
};

// S697 — Plan types that use the ARR attainment rate table engine (S28.11.1)
// RateTableManager is rendered in the Configure step only for these plan types.
// Other plan types with rate table structures use their own dedicated managers.
const RATE_TABLE_PLAN_TYPES = new Set([
  'arr_attainment',
  'revenue_type_differentiated',
  'territory_based',
  'overlay_specialist',
  'grr_nrr_retention',
  'draw_guarantee',
]);

const AMENDMENT_REASON_LABELS = {
  plan_design:         'Plan Design Change',
  rate_change:         'Rate Change',
  data_correction:     'Administrative Correction',
  deployment_override: 'Administrative Correction',
};

// ---------------------------------------------------------------------------
// S693 — Wizard step definitions (spec S26.5)
// PATH A (Library): Source → Plan Details → Configure → Review → Submit
// PATH B (Custom):  Plan Type → Plan Details → Configure → Review → Submit
// ---------------------------------------------------------------------------

const WIZARD_STEPS_PATH_A = [
  { key: 'source',      label: 'Source'       },
  { key: 'details',     label: 'Plan Details' },
  { key: 'configure',   label: 'Configure'    },
  { key: 'review',      label: 'Review'       },
  { key: 'submit',      label: 'Submit'       },
];

const WIZARD_STEPS_PATH_B = [
  { key: 'plan_type',   label: 'Plan Type'    },
  { key: 'details',     label: 'Plan Details' },
  { key: 'configure',   label: 'Configure'    },
  { key: 'review',      label: 'Review'       },
  { key: 'submit',      label: 'Submit'       },
];

// ---------------------------------------------------------------------------
// Shared CSS
// ---------------------------------------------------------------------------

const btnSecondary =
  'rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] ' +
  'px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] ' +
  'hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';

const btnPrimary =
  'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium ' +
  'text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';

const btnWarning =
  'rounded-lg bg-[rgb(var(--color-warning))] px-3 py-1.5 text-tok-sm font-medium ' +
  'text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed';

const btnDanger =
  'rounded-lg border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/5 ' +
  'px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-danger))] ' +
  'hover:bg-[rgb(var(--color-danger))]/10 disabled:opacity-50 disabled:cursor-not-allowed';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveModifierState(records) {
  if (!records || records.length === 0) return 'not_started';
  const states = records.map((r) => {
    if (r.approved_by || r.adjustment_status === 'approved') return 'approved';
    if (r.submitted_at || r.submitted_by || r.adjustment_status === 'pending_approval') return 'pending_approval';
    if (r.adjustment_status === 'rejected') return 'rejected';
    return 'draft';
  });
  if (states.includes('approved'))         return 'approved';
  if (states.includes('pending_approval')) return 'pending_approval';
  if (states.includes('rejected'))         return 'rejected';
  return 'draft';
}

function getErrorMessage(error, fallback) { return error?.message || fallback; }

function shortId(id) {
  if (!id) return '—';
  const s = String(id);
  return s.length > 8 ? s.slice(0, 8) : s;
}

const STATE_STYLE = {
  not_started:      { dot: 'bg-[rgb(var(--color-border))]',   label: 'Not Started',      text: 'text-[rgb(var(--color-text-muted))]' },
  draft:            { dot: 'bg-[rgb(var(--color-warning))]',  label: 'Draft',            text: 'text-[rgb(var(--color-warning))]'    },
  pending_approval: { dot: 'bg-[rgb(var(--color-info))]',     label: 'Pending Approval', text: 'text-[rgb(var(--color-info))]'       },
  approved:         { dot: 'bg-[rgb(var(--color-success))]',  label: 'Approved',         text: 'text-[rgb(var(--color-success))]'    },
  rejected:         { dot: 'bg-[rgb(var(--color-danger))]',   label: 'Rejected',         text: 'text-[rgb(var(--color-danger))]'     },
};

const PLAN_STATUS_STYLE = {
  draft:            'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]',
  pending_approval: 'bg-[rgb(var(--color-info))]/10 text-[rgb(var(--color-info))]',
  approved:         'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]',
  archived:         'bg-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))]',
};

function fmtCurrency(code) { return code ?? '—'; }

// ---------------------------------------------------------------------------
// ModifierCard
// ---------------------------------------------------------------------------

function ModifierCard({ card, state, hasFxWarning, isActive, onClick }) {
  const style = STATE_STYLE[state] ?? STATE_STYLE.not_started;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
      className={[
        'relative flex flex-col gap-2 rounded-xl border p-4 cursor-pointer transition-all focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]',
        isActive
          ? 'border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/5'
          : 'border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] hover:border-[rgb(var(--color-primary))]/40 hover:bg-[rgb(var(--color-surface-muted))]',
      ].join(' ')}
    >
      {hasFxWarning && (
        <div className="absolute top-3 right-3 flex items-center gap-1 rounded-full bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-2 py-0.5">
          <svg className="h-3 w-3 text-[rgb(var(--color-warning))]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.963-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <span className="text-tok-xs font-medium text-[rgb(var(--color-warning))]">FX Pending</span>
        </div>
      )}
      <p className="text-tok-sm font-medium text-[rgb(var(--color-text))] pr-20">{card.label}</p>
      <div className="flex items-center gap-1.5">
        <span className={`h-2 w-2 rounded-full shrink-0 ${style.dot}`} />
        <span className={`text-tok-xs font-medium ${style.text}`}>{style.label}</span>
      </div>
      {state === 'not_started' && (
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
          Not Started — Available to configure.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ComponentPillStrip
// ---------------------------------------------------------------------------

function ComponentPillStrip({ components, selectedId, onSelect, roleMap, segmentMap }) {
  const containerRef = useRef(null);
  const [showOverflow, setShowOverflow] = useState(false);
  const PILL_LIMIT = 8;

  const visible  = components.slice(0, PILL_LIMIT);
  const overflow = components.slice(PILL_LIMIT);

  function labelFor(c) {
    const role = roleMap[c.role_id];
    const seg  = segmentMap[c.segment_id];
    return [role?.role_code ?? role?.role_name, seg?.segment_code ?? seg?.segment_name]
      .filter(Boolean).join(' · ') || `Component ${c.plan_component_id}`;
  }

  return (
    <div className="flex items-center gap-2 flex-wrap relative" ref={containerRef}>
      {visible.map((c) => {
        const isActive = String(c.plan_component_id) === String(selectedId);
        return (
          <button
            key={c.plan_component_id}
            type="button"
            title={labelFor(c)}
            onClick={() => onSelect(c.plan_component_id)}
            className={[
              'rounded-full border px-3 py-1 text-tok-xs font-medium transition-colors truncate',
              'max-w-[180px] min-w-[96px] whitespace-nowrap overflow-hidden',
              isActive
                ? 'border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))] text-white'
                : 'border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text-muted))] hover:border-[rgb(var(--color-primary))]/50 hover:text-[rgb(var(--color-text))]',
            ].join(' ')}
          >
            {labelFor(c)}
          </button>
        );
      })}
      {overflow.length > 0 && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowOverflow((v) => !v)}
            className="rounded-full border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
          >
            +{overflow.length} More ▼
          </button>
          {showOverflow && (
            <div className="absolute top-full left-0 z-20 mt-1 w-48 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-lg py-1">
              {overflow.map((c) => (
                <button
                  key={c.plan_component_id}
                  type="button"
                  onClick={() => { onSelect(c.plan_component_id); setShowOverflow(false); }}
                  className={[
                    'w-full text-left px-3 py-2 text-tok-xs hover:bg-[rgb(var(--color-surface-muted))]',
                    String(c.plan_component_id) === String(selectedId)
                      ? 'text-[rgb(var(--color-primary))] font-medium'
                      : 'text-[rgb(var(--color-text))]',
                  ].join(' ')}
                >
                  {labelFor(c)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActionsDropdown — replaces CreateChangeModal (CHANGE-04 / S724)
// Shown when plan is approved and no open amendment.
// Conflict guard: Amend Plan Economics disabled when modifier pending_approval (AD-173).
// ---------------------------------------------------------------------------

function ActionsDropdown({
  plan, applicabilityRows,
  onAmendPlanEconomics, onAddModifier, onFixPlanDetails, onUpdateTemplate, onSaveAsTemplate
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const hasModifierPendingApproval = applicabilityRows.some(
    (r) => r.modifier_status === 'pending_approval'
  );

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const items = [
    {
      label: 'Amend Plan Economics',
      sublabel: hasModifierPendingApproval
        ? 'A modifier is awaiting CA-2 approval. Resolve before amending plan economics.'
        : 'Change weights, rates, or component configuration. Requires CA-2 reapproval.',
      onClick: () => { setOpen(false); onAmendPlanEconomics(); },
      tone: 'warning',
      disabled: hasModifierPendingApproval,
      disabledReason: 'Resolve modifier approval before amending plan economics.',
    },
    {
      label: 'Add Modifier',
      // CHANGE-13 Part A1 S737: Corrected per AD-169.
      // Add Modifier on an approved plan does NOT open an amendment — new modifier
      // has its own lifecycle. Removing amendment language from sublabel (L-127).
      sublabel: 'Add a new incentive modifier to this approved plan. ' +
                'The new modifier follows its own approval lifecycle — no amendment needed.',
      onClick: () => { setOpen(false); onAddModifier(); },
      tone: 'primary',
      disabled: false,
    },
    {
      label: 'Fix Plan Details',
      sublabel: 'Correct name, code, or description only. No payout effect. ' +
                'No reapproval required. Commits immediately.',
      onClick: () => { setOpen(false); onFixPlanDetails(); },
      tone: 'default',
      disabled: false,
    },
    {
      label: 'Update Template for Future Plans',
      sublabel: plan?.template_id
        ? 'Create a new draft version of the originating template. ' +
          'Does not change this deployed plan.'
        : null,
      onClick: () => { setOpen(false); onUpdateTemplate(); },
      tone: 'default',
      disabled: !plan?.template_id,
      disabledReason: 'This plan was not deployed from a library template.',
    },
    {
      // CHANGE-13 Part B S737: Save as Template inside ActionsDropdown (AD-198, S31.8).
      // Gate: plan must be approved AND have at least one assignment (assignment_count > 0).
      // Visible but disabled until gate met — disabled explanation shown as sublabel.
      label: 'Save as Template',
      sublabel: (plan?.assignment_count ?? 0) > 0
        ? 'Promote this approved plan to a reusable template for future deployments.'
        : 'Assign this approved plan to at least one position before saving it as a template.',
      onClick: () => { setOpen(false); onSaveAsTemplate(); },
      tone: 'default',
      disabled: (plan?.assignment_count ?? 0) === 0,
      disabledReason: 'Assign this approved plan to at least one position before saving it as a template.',
    },
  ];

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)}
              className={btnPrimary + ' flex items-center gap-1.5'}>
        Actions
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-80 rounded-xl border
                        border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]
                        shadow-xl overflow-hidden">
          {items.map((item) => (
            <button key={item.label} type="button"
                    onClick={item.disabled ? undefined : item.onClick}
                    disabled={item.disabled}
                    className={[
                      'w-full text-left px-4 py-3 flex flex-col gap-0.5 transition-colors',
                      'border-b border-[rgb(var(--color-border))] last:border-b-0',
                      item.disabled
                        ? 'opacity-40 cursor-not-allowed bg-[rgb(var(--color-surface-muted))]'
                        : 'hover:bg-[rgb(var(--color-surface-muted))] cursor-pointer',
                    ].join(' ')}>
              <span className={[
                'text-tok-sm font-semibold',
                item.tone === 'warning' ? 'text-[rgb(var(--color-warning))]'
                : item.tone === 'primary' ? 'text-[rgb(var(--color-primary))]'
                : 'text-[rgb(var(--color-text))]',
              ].join(' ')}>{item.label}</span>
              <span className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
                {item.disabled
                  ? (item.disabledReason ?? item.sublabel)
                  : item.sublabel}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddModifierPicker — type picker with full 5-state matrix (CHANGE-05 / S724)
// Shown after "Add Modifier" in ActionsDropdown.
// not_applicable → hidden | approved → hidden (read-only view handles it)
// draft → "Continue setup" | pending_approval → disabled, labelled
// not_started → selectable, primary creation path
// ---------------------------------------------------------------------------

function AddModifierPicker({ open, applicabilityRows, onSelect, onCancel }) {
  if (!open) return null;

  const MODIFIER_OPTIONS = [
    { type: 'P3B-KICKER',   label: 'Kicker / Multiplier',         key: 'kicker'    },
    { type: 'P3B-DRAW',     label: 'Draw & Guarantee',             key: 'draw'      },
    { type: 'P3B-SPIFF',    label: 'SPIFF / Tactical',             key: 'spiff'     },
    { type: 'P3B-CLAWBACK', label: 'Clawback Policy',              key: 'clawback'  },
    { type: 'P3B-REVTYPE',  label: 'Revenue Type Differentials',   key: 'rev-rates' },
    { type: 'P3B-CAP',      label: 'Accelerator Cap',              key: 'caps'      },
    { type: 'P3B-SPLIT',    label: 'Deal Splits',                  key: 'deal-split'},
  ];

  const enriched = MODIFIER_OPTIONS.map((opt) => {
    const row = applicabilityRows.find((r) => r.modifier_type === opt.type);
    const status = row?.modifier_status ?? 'not_started';
    const applicability = row?.applicability ?? 'optional';

    if (applicability === 'not_applicable') return null;
    if (status === 'approved') return null;

    let sublabel = null;
    let disabled = false;

    if (status === 'draft') {
      sublabel = 'Continue setup — draft already exists.';
    } else if (status === 'pending_approval') {
      sublabel = 'Awaiting CA-2 approval.';
      disabled = true;
    }

    return { ...opt, sublabel, disabled };
  }).filter(Boolean);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         onClick={(e) => e.stopPropagation()}>
      <div className="w-full max-w-sm rounded-2xl border border-[rgb(var(--color-border))]
                      bg-[rgb(var(--color-surface))] shadow-2xl">
        <div className="border-b border-[rgb(var(--color-border))] px-5 py-4">
          <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
            Add Modifier
          </h2>
          <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Select modifier type. Each modifier has its own approval lifecycle.
            This plan remains approved.
          </p>
        </div>
        <div className="p-3 flex flex-col gap-1">
          {enriched.length === 0 && (
            <p className="px-2 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
              No modifiers available to add for this plan type.
            </p>
          )}
          {enriched.map((opt) => (
            <button key={opt.key} type="button"
                    onClick={opt.disabled ? undefined : () => onSelect(opt.key)}
                    disabled={opt.disabled}
                    className={[
                      'w-full text-left rounded-xl border px-4 py-3 flex flex-col gap-0.5',
                      opt.disabled
                        ? 'border-[rgb(var(--color-border))] opacity-50 cursor-not-allowed'
                        : 'border-[rgb(var(--color-border))] hover:border-[rgb(var(--color-primary))]/40 hover:bg-[rgb(var(--color-surface-muted))] transition-colors cursor-pointer',
                    ].join(' ')}>
              <span className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {opt.label}
              </span>
              {opt.sublabel && (
                <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                  {opt.sublabel}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="border-t border-[rgb(var(--color-border))] px-5 py-4 flex justify-end">
          <button type="button" onClick={onCancel} className={btnSecondary}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StageImpactPreview — shown before AmendmentBeginModal (§7A Step 1)
// ---------------------------------------------------------------------------

function StageImpactPreview({ open, stageImpactMap, loading, error, onBegin, onCancel, assignedPositionCount, periodLabel }) {
  if (!open) return null;

  const affectedStages = (stageImpactMap ?? []).filter(
    (entry) => entry.amended_entity_type === 'plan_component' || entry.entity_type === 'plan_component'
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={(e) => e.stopPropagation()}>
      <div className="w-full max-w-md rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-2xl">
        <div className="border-b border-[rgb(var(--color-border))] px-5 py-4">
          <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Amendment Impact Preview</h2>
        </div>
        <div className="p-5 flex flex-col gap-4">
          {loading && (
            <div className="animate-pulse space-y-2">
              <div className="h-4 rounded bg-[rgb(var(--color-surface-muted))]" />
              <div className="h-4 rounded bg-[rgb(var(--color-surface-muted))] w-3/4" />
            </div>
          )}
          {error && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
              {error}
            </div>
          )}
          {!loading && !error && (
            <>
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                Changing a plan component will require reapproval of:
              </p>
              {affectedStages.length > 0 ? (
                <ul className="flex flex-col gap-1.5">
                  {affectedStages.map((entry, i) => (
                    <li key={i} className="flex items-center gap-2 text-tok-sm text-[rgb(var(--color-text))]">
                      <svg className="h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.963-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      {entry.stage_label ?? entry.stage_name ?? `Stage ${entry.stage_id ?? i + 1}`}
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className="flex flex-col gap-1.5">
                  {['Plan Approval (Stage 10)', 'Population Snapshot (Stage 15)', 'Calculation (Stage 16)'].map((s) => (
                    <li key={s} className="flex items-center gap-2 text-tok-sm text-[rgb(var(--color-text))]">
                      <svg className="h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.963-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      {s}
                    </li>
                  ))}
                </ul>
              )}
              <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))] flex flex-col gap-1">
                {assignedPositionCount != null && (
                  <span>Positions affected: <strong className="text-[rgb(var(--color-text))]">{assignedPositionCount}</strong> assigned to this plan</span>
                )}
                {periodLabel && (
                  <span>Fiscal Period: <strong className="text-[rgb(var(--color-text))]">{periodLabel}</strong></span>
                )}
              </div>
            </>
          )}
        </div>
        <div className="border-t border-[rgb(var(--color-border))] px-5 py-4 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className={btnSecondary} disabled={loading}>Cancel</button>
          <button type="button" onClick={onBegin} className={btnPrimary} disabled={loading}>Begin Amendment</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CommitConfirmModal — pre-commit confirmation (§7A Step 5)
// ---------------------------------------------------------------------------

function CommitConfirmModal({ open, amendmentId, reasonCode, reasonNote, stageImpactMap, onConfirm, onKeepEditing, committing, commitError }) {
  if (!open) return null;

  const affectedCount = (stageImpactMap ?? []).filter(
    (e) => e.amended_entity_type === 'plan_component' || e.entity_type === 'plan_component'
  ).length || 3;

  const reasonLabel = AMENDMENT_REASON_LABELS[reasonCode] ?? reasonCode ?? '—';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={(e) => e.stopPropagation()}>
      <div className="w-full max-w-md rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-2xl">
        <div className="border-b border-[rgb(var(--color-border))] px-5 py-4">
          <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Commit Amendment</h2>
          <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
            A-{shortId(amendmentId)} · {reasonLabel}
          </p>
        </div>
        <div className="p-5 flex flex-col gap-3">
          <div className="rounded-lg bg-[rgb(var(--color-warning))]/5 border border-[rgb(var(--color-warning))]/20 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
            Committing will invalidate {affectedCount} wizard stage{affectedCount !== 1 ? 's' : ''} and trigger reapproval.
            This action cannot be undone.
          </div>
          {reasonNote && (
            <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
              Note: {reasonNote}
            </div>
          )}
          {commitError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
              {commitError}
            </div>
          )}
        </div>
        <div className="border-t border-[rgb(var(--color-border))] px-5 py-4 flex justify-end gap-2">
          <button type="button" onClick={onKeepEditing} disabled={committing} className={btnSecondary}>
            Keep Editing
          </button>
          <button type="button" onClick={onConfirm} disabled={committing} className={btnWarning}>
            {committing ? 'Committing…' : 'Commit Amendment'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AmendmentStatusPills — header pills for open/committed amendments (§7A)
// ---------------------------------------------------------------------------

function AmendmentStatusPills({ amendments, openAmendmentId, onClickCommitted }) {
  const open      = amendments.filter((a) => a.amendment_status === 'open');
  const committed = amendments.filter((a) => a.amendment_status === 'committed');

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {open.map((a) => (
        <span
          key={a.amendment_id}
          className="inline-flex items-center rounded-full border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-warning))]"
        >
          Amendment Open · A-{shortId(a.amendment_id)}
        </span>
      ))}
      {committed.slice(0, 3).map((a) => (
        <button
          key={a.amendment_id}
          type="button"
          onClick={onClickCommitted}
          className="inline-flex items-center rounded-full border border-[rgb(var(--color-info))]/30 bg-[rgb(var(--color-info))]/5 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-info))] hover:bg-[rgb(var(--color-info))]/10"
        >
          A-{shortId(a.amendment_id)} Committed
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SaveAsTemplateWizard — §9 Save as Template — four-step guided wizard
//
// Step 1 — Review Source (immutable)
// Step 2 — Template Metadata (auto-populated, editable)
// Step 3 — Review Generated Package
// Step 4 — Create Draft (governance notice + submit)
//
// POST /api/v1/template-library/templates/from-plan/{plan_id}
// Body: TemplateFromPlanRequest { template_name?, template_code?, description?, tags? }
// Response 201: { template_id, template_version_id, template_name, ... }
//
// §9 Critical Governance Rule: plan approval NEVER inherited by template.
// Template always starts as draft. Two-actor approval (NN-36) required separately.
// ---------------------------------------------------------------------------

const WIZARD_STEPS = [
  { num: 1, label: 'Review Source'     },
  { num: 2, label: 'Template Metadata' },
  { num: 3, label: 'Review Package'    },
  { num: 4, label: 'Create Draft'      },
];

function SaveAsTemplateWizard({ open, plan, components, onClose, onCreated }) {
  const [step,        setStep]        = useState(1);
  const [submitting,  setSubmitting]  = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result,      setResult]      = useState(null); // POST 201 response

  // Step 2 — editable metadata
  // §9: auto-populated name ({plan_name} Template) and code (TPL-{plan_code})
  const autoName = plan ? `${plan.plan_name} Template` : '';
  const autoCode = plan?.plan_code ? `TPL-${plan.plan_code}` : '';

  const [templateName, setTemplateName] = useState('');
  const [templateCode, setTemplateCode] = useState('');
  const [description,  setDescription]  = useState('');
  const [tagsRaw,      setTagsRaw]      = useState(''); // comma-separated input

  // Reset wizard state each time it opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setSubmitting(false);
      setSubmitError('');
      setResult(null);
      setTemplateName(autoName);
      setTemplateCode(autoCode);
      setDescription('');
      setTagsRaw('');
    }
  }, [open]); // eslint-disable-line

  if (!open || !plan) return null;

  const componentCount = components?.length ?? 0;
  const planStatus     = plan.plan_status ?? 'draft';
  const tags           = tagsRaw.split(',').map((t) => t.trim()).filter(Boolean);

  async function handleCreateDraft() {
    setSubmitting(true);
    setSubmitError('');
    try {
      const body = {};
      // Only include fields the user explicitly set or that differ from auto-populated defaults
      if (templateName.trim())  body.template_name = templateName.trim();
      if (templateCode.trim())  body.template_code = templateCode.trim();
      if (description.trim())   body.description   = description.trim();
      if (tags.length > 0)      body.tags          = tags;

      const data = await apiPost(
        `/api/v1/template-library/templates/from-plan/${plan.plan_id}`,
        body
      );
      setResult(data);
      // Signal parent to show toast + re-load
      onCreated(data);
    } catch (err) {
      setSubmitError(getErrorMessage(err, 'Failed to create template draft. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  // Step indicator bar
  function StepBar() {
    return (
      <div className="flex items-center gap-0">
        {WIZARD_STEPS.map((s, idx) => {
          const isCurrent  = step === s.num;
          const isComplete = step > s.num;
          return (
            <React.Fragment key={s.num}>
              <div className="flex flex-col items-center gap-1">
                <div className={[
                  'flex h-7 w-7 items-center justify-center rounded-full text-tok-xs font-semibold transition-colors',
                  isCurrent  ? 'bg-[rgb(var(--color-primary))] text-white'
                  : isComplete ? 'bg-[rgb(var(--color-success))] text-white'
                  : 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]',
                ].join(' ')}>
                  {isComplete
                    ? <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                    : s.num
                  }
                </div>
                <span className={`hidden sm:block text-tok-xs whitespace-nowrap ${isCurrent ? 'font-semibold text-[rgb(var(--color-text))]' : 'text-[rgb(var(--color-text-muted))]'}`}>
                  {s.label}
                </span>
              </div>
              {idx < WIZARD_STEPS.length - 1 && (
                <div className={`h-0.5 flex-1 mx-2 ${isComplete ? 'bg-[rgb(var(--color-success))]' : 'bg-[rgb(var(--color-border))]'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="w-full max-w-xl rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-2xl flex flex-col max-h-[90vh]">

        {/* Modal header */}
        <div className="border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Save as Template</h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
              aria-label="Close"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <StepBar />
        </div>

        {/* Step content — scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-5">

          {/* ── Step 1 — Review Source ── */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                Review the source plan before promoting it to a reusable template.
                This information is read-only and will be captured in the template record.
              </p>

              <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] divide-y divide-[rgb(var(--color-border))]">
                <div className="grid grid-cols-2 gap-x-4 px-4 py-3">
                  <div>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Plan Name</p>
                    <p className="mt-0.5 text-tok-sm font-medium text-[rgb(var(--color-text))]">{plan.plan_name ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Plan Code</p>
                    <p className="mt-0.5 font-mono text-tok-sm text-[rgb(var(--color-text))]">{plan.plan_code ?? '—'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-4 px-4 py-3">
                  <div>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Approval Status</p>
                    <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-success))] font-medium capitalize">
                      {planStatus.replace(/_/g, ' ')}
                    </p>
                  </div>
                  <div>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Version</p>
                    <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text))]">
                      {plan.version != null ? `v${plan.version}` : '—'}
                    </p>
                  </div>
                </div>
                <div className="px-4 py-3">
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Plan Components</p>
                  <p className="mt-0.5 text-tok-sm font-medium text-[rgb(var(--color-text))]">
                    {componentCount} component{componentCount !== 1 ? 's' : ''}
                  </p>
                </div>
                {plan.fiscal_period_id && (
                  <div className="px-4 py-3">
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Fiscal Period</p>
                    <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text))]">ID {plan.fiscal_period_id}</p>
                  </div>
                )}
              </div>

              {/* §9 origin callout */}
              <div className="flex items-start gap-2 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
                <svg className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-text-muted))]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                  The new template will be marked <strong className="text-[rgb(var(--color-text))]">Derived from Production</strong>.
                  This origin is preserved in the template record for library governance.
                </p>
              </div>
            </div>
          )}

          {/* ── Step 2 — Template Metadata ── */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                These fields are auto-populated from the source plan. Rename or add tags as needed.
                Under one minute — all fields are optional.
              </p>

              {/* Template Name */}
              <label className="flex flex-col gap-1">
                <span className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                  Template Name
                  <span className="ml-1 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">(auto-populated)</span>
                </span>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder={autoName}
                  className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] placeholder:italic placeholder:text-[rgb(var(--color-placeholder))] focus:border-[rgb(var(--color-primary))] focus:outline-none"
                />
              </label>

              {/* Template Code */}
              <label className="flex flex-col gap-1">
                <span className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                  Template Code
                  <span className="ml-1 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">(auto-populated)</span>
                </span>
                <input
                  type="text"
                  value={templateCode}
                  onChange={(e) => setTemplateCode(e.target.value)}
                  placeholder={autoCode}
                  className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 font-mono text-tok-sm text-[rgb(var(--color-text))] placeholder:italic placeholder:text-[rgb(var(--color-placeholder))] focus:border-[rgb(var(--color-primary))] focus:outline-none"
                />
              </label>

              {/* Description */}
              <label className="flex flex-col gap-1">
                <span className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                  Description
                  <span className="ml-1 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">(optional)</span>
                </span>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Describe when and why to use this template…"
                  className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] placeholder:italic placeholder:text-[rgb(var(--color-placeholder))] focus:border-[rgb(var(--color-primary))] focus:outline-none resize-none"
                />
              </label>

              {/* Tags */}
              <label className="flex flex-col gap-1">
                <span className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                  Tags
                  <span className="ml-1 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">(optional, comma-separated)</span>
                </span>
                <input
                  type="text"
                  value={tagsRaw}
                  onChange={(e) => setTagsRaw(e.target.value)}
                  placeholder="e.g. enterprise, AE, ARR"
                  className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] placeholder:italic placeholder:text-[rgb(var(--color-placeholder))] focus:border-[rgb(var(--color-primary))] focus:outline-none"
                />
                {tags.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center rounded-full border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-2.5 py-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </label>
            </div>
          )}

          {/* ── Step 3 — Review Generated Package ── */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                Review the package that will be generated from this plan.
                Confirm the component count and origin before creating the draft.
              </p>

              <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] divide-y divide-[rgb(var(--color-border))]">
                <div className="grid grid-cols-2 gap-x-4 px-4 py-3">
                  <div>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Template Name</p>
                    <p className="mt-0.5 text-tok-sm font-medium text-[rgb(var(--color-text))]">
                      {templateName.trim() || autoName || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Template Code</p>
                    <p className="mt-0.5 font-mono text-tok-sm text-[rgb(var(--color-text))]">
                      {templateCode.trim() || autoCode || '—'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-4 px-4 py-3">
                  <div>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Components</p>
                    <p className="mt-0.5 text-tok-sm font-medium text-[rgb(var(--color-text))]">
                      {componentCount} component{componentCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Template Origin</p>
                    {/* §9: Never expose "reverse_engineered" label directly */}
                    <span className="mt-0.5 inline-flex items-center rounded-full border border-purple-200 bg-purple-50 px-2.5 py-0.5 text-tok-xs font-medium text-purple-700">
                      Derived from Production
                    </span>
                  </div>
                </div>
                <div className="px-4 py-3">
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Initial Status</p>
                  <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text-muted))] font-medium">Draft — awaiting approval</p>
                </div>
                {description.trim() && (
                  <div className="px-4 py-3">
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Description</p>
                    <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text))]">{description.trim()}</p>
                  </div>
                )}
                {tags.length > 0 && (
                  <div className="px-4 py-3">
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Tags</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {tags.map((tag) => (
                        <span key={tag} className="inline-flex items-center rounded-full border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2.5 py-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Validation warnings placeholder — static, no mock data */}
              <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
                <p className="text-tok-xs font-semibold text-[rgb(var(--color-text))]">Validation</p>
                <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                  No validation warnings. The server will validate configuration on creation.
                </p>
              </div>
            </div>
          )}

          {/* ── Step 4 — Create Draft ── */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] divide-y divide-[rgb(var(--color-border))]">
                <div className="px-4 py-3">
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Source Plan</p>
                  <p className="mt-0.5 text-tok-sm font-medium text-[rgb(var(--color-text))]">{plan.plan_name}</p>
                </div>
                <div className="px-4 py-3">
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">New Template</p>
                  <p className="mt-0.5 text-tok-sm font-medium text-[rgb(var(--color-text))]">
                    {templateName.trim() || autoName || '—'}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-x-4 px-4 py-3">
                  <div>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Origin</p>
                    <span className="mt-0.5 inline-flex items-center rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-tok-xs font-medium text-purple-700">
                      Derived from Production
                    </span>
                  </div>
                  <div>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Status after creation</p>
                    <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text-muted))]">Draft</p>
                  </div>
                </div>
              </div>

              {/* §9 Critical Governance Rule — always visible on Step 4, non-dismissible */}
              <div className="rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 px-4 py-4">
                <div className="flex items-start gap-3">
                  <svg className="mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--color-warning))]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <div>
                    <p className="text-tok-sm font-semibold text-[rgb(var(--color-warning))]">
                      Governance Notice — Approval Not Transferred
                    </p>
                    <p className="mt-1 text-tok-xs text-[rgb(var(--color-warning))]/90 leading-relaxed">
                      This plan's approval is not transferred to the template.
                      The new template draft requires separate two-actor approval (NN-36) before
                      it can be deployed to any fiscal period. "This plan was approved for FY26
                      deployment" and "This design is approved for future reuse" are distinct
                      governance events.
                    </p>
                  </div>
                </div>
              </div>

              {submitError && (
                <div role="alert" className="rounded-lg border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                  {submitError}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer navigation */}
        <div className="border-t border-[rgb(var(--color-border))] px-5 py-4 shrink-0 flex items-center justify-between">
          <div>
            {step > 1 && !result && (
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                disabled={submitting}
                className={btnSecondary}
              >
                Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className={btnSecondary}
            >
              {result ? 'Close' : 'Cancel'}
            </button>
            {!result && step < 4 && (
              <button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                className={btnPrimary}
              >
                {step === 3 ? 'Review & Create' : 'Next'}
              </button>
            )}
            {!result && step === 4 && (
              <button
                type="button"
                onClick={handleCreateDraft}
                disabled={submitting}
                className={btnPrimary}
              >
                {submitting ? 'Creating…' : 'Create Draft Template'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// VersionTimeline — §7 vertical chronology, categorised events
//
// Six categories, never mixed in one generic audit stream:
//   Template Version | Deployment | Amendment | Administrative Correction |
//   Approval | Acknowledgment
//
// Data sources (all already in state):
//   plan          — approved_at, approved_by, version, plan_status, created_at,
//                   acknowledgment_status, acknowledged_by, acknowledged_count
//   planAmendments — amendment_id, amendment_status, amendment_reason_code,
//                    amendment_reason_note, created_at, committed_at
//   period        — period_label for Deployment event
// ---------------------------------------------------------------------------

const TIMELINE_CATEGORY_STYLE = {
  template_version:         { color: 'text-[rgb(var(--color-primary))]',  dot: 'bg-[rgb(var(--color-primary))]',  label: 'Template Version'           },
  deployment:               { color: 'text-[rgb(var(--color-info))]',     dot: 'bg-[rgb(var(--color-info))]',     label: 'Deployment'                 },
  amendment:                { color: 'text-[rgb(var(--color-warning))]',  dot: 'bg-[rgb(var(--color-warning))]',  label: 'Amendment'                  },
  administrative_correction:{ color: 'text-[rgb(var(--color-text-muted))]',dot:'bg-[rgb(var(--color-text-muted))]',label:'Administrative Correction'  },
  approval:                 { color: 'text-[rgb(var(--color-success))]',  dot: 'bg-[rgb(var(--color-success))]',  label: 'Approval'                   },
  acknowledgment:           { color: 'text-[rgb(var(--color-text))]',     dot: 'bg-[rgb(var(--color-border))]',   label: 'Acknowledgment'             },
};

function fmtDate(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return ts; }
}

function VersionTimeline({ plan, amendments, period }) {
  const [expanded, setExpanded] = React.useState(true);

  // Build ordered event list from available data
  const events = React.useMemo(() => {
    const list = [];

    // Template Version event — plan creation / version baseline
    if (plan?.created_at) {
      list.push({
        category: 'template_version',
        title: plan.version != null ? `Version ${plan.version} Created` : 'Plan Created',
        detail: null,
        ts: plan.created_at,
      });
    }

    // Deployment event — tied to fiscal period
    if (plan?.created_at && period) {
      list.push({
        category: 'deployment',
        title: `Deployed — ${period.period_label ?? period.period_code ?? `Period ${period.fiscal_period_id}`}`,
        detail: plan.assigned_position_count != null ? `${plan.assigned_position_count} positions` : null,
        ts: plan.created_at,
      });
    }

    // Approval event
    if (plan?.approved_at) {
      list.push({
        category: 'approval',
        title: 'Plan Approved',
        detail: plan.approved_by ? `By ${plan.approved_by}` : null,
        ts: plan.approved_at,
      });
    }

    // Acknowledgment event
    if (plan?.acknowledged_at ?? plan?.acknowledgment_completed_at) {
      const ackAt = plan.acknowledged_at ?? plan.acknowledgment_completed_at;
      const ackCount = plan.acknowledged_count ?? null;
      list.push({
        category: 'acknowledgment',
        title: 'Acknowledgment Completed',
        detail: ackCount != null ? `${ackCount} rep${ackCount !== 1 ? 's' : ''}` : null,
        ts: ackAt,
      });
    }

    // Amendment events — one entry per amendment, split by type
    amendments.forEach((a) => {
      const isCorrection = a.amendment_reason_code === 'data_correction' || a.amendment_reason_code === 'deployment_override';
      const cat = isCorrection ? 'administrative_correction' : 'amendment';
      const statusLabel = a.amendment_status === 'committed' ? 'Committed' : a.amendment_status === 'open' ? 'Open' : a.amendment_status ?? '—';
      list.push({
        category: cat,
        title: `${isCorrection ? 'Administrative Correction' : 'Amendment'} A-${shortId(a.amendment_id)}`,
        detail: [
          a.amendment_reason_note ?? null,
          statusLabel,
        ].filter(Boolean).join(' · '),
        ts: a.committed_at ?? a.created_at ?? null,
      });
    });

    // Sort newest first
    return list.sort((a, b) => {
      if (!a.ts && !b.ts) return 0;
      if (!a.ts) return 1;
      if (!b.ts) return -1;
      return new Date(b.ts) - new Date(a.ts);
    });
  }, [plan, amendments, period]);

  if (events.length === 0) return null;

  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between px-5 py-4 text-left"
      >
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
          <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Version Timeline</span>
          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">({events.length} events)</span>
        </div>
        <svg
          className={`h-4 w-4 text-[rgb(var(--color-text-muted))] transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="border-t border-[rgb(var(--color-border))] px-5 py-4">
          <div className="relative space-y-0">
            {/* Vertical line */}
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-[rgb(var(--color-border))]" />

            {events.map((ev, idx) => {
              const style = TIMELINE_CATEGORY_STYLE[ev.category] ?? TIMELINE_CATEGORY_STYLE.amendment;
              return (
                <div key={idx} className="relative flex gap-4 pb-5 last:pb-0">
                  {/* Category dot */}
                  <div className={`relative z-10 mt-1 h-3.5 w-3.5 shrink-0 rounded-full border-2 border-[rgb(var(--color-surface))] ${style.dot}`} />
                  <div className="min-w-0 flex-1">
                    {/* Category label */}
                    <p className={`text-tok-xs font-semibold uppercase tracking-wider ${style.color}`}>
                      {style.label}
                    </p>
                    {/* Event title */}
                    <p className="mt-0.5 text-tok-sm font-medium text-[rgb(var(--color-text))]">
                      {ev.title}
                    </p>
                    {/* Detail + date row */}
                    <div className="mt-0.5 flex flex-wrap items-center gap-2">
                      {ev.detail && (
                        <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{ev.detail}</span>
                      )}
                      {ev.ts && (
                        <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                          {ev.detail ? '·' : ''} {fmtDate(ev.ts)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// VersionMatrix — §7 tabular version history
//
// Columns: Version | Status | Deployment | Amendments | Rep Ack | Current
// Source: plan object for current version row.
// plan.version_history array if present (future); single row fallback today.
// ---------------------------------------------------------------------------

function VersionMatrix({ plan, amendments, period }) {
  const [expanded, setExpanded] = React.useState(true);

  // Build version rows — use plan.version_history if backend provides it,
  // otherwise fall back to single current-version row
  const rows = React.useMemo(() => {
    // If backend returns version history array, use it
    if (Array.isArray(plan?.version_history) && plan.version_history.length > 0) {
      return plan.version_history.map((v) => ({
        version:     v.version ?? v.version_number ?? '—',
        status:      v.plan_status ?? v.status ?? '—',
        deployment:  v.fiscal_period_label ?? v.period_label ?? (period?.period_label ?? '—'),
        amendments:  v.amendment_count ?? 0,
        repAck:      v.acknowledgment_status ?? '—',
        isCurrent:   v.is_current ?? false,
      }));
    }

    // Single row fallback from current plan object
    const committedAmendments = amendments.filter((a) => a.amendment_status === 'committed' && a.amendment_reason_code !== 'data_correction').length;
    const ackStatus = plan?.acknowledged_count != null
      ? `${plan.acknowledged_count}/${plan.assigned_position_count ?? plan.acknowledged_count}`
      : (plan?.acknowledgment_status ?? '—');

    return [{
      version:    plan?.version != null ? String(plan.version) : '1',
      status:     plan?.plan_status ?? '—',
      deployment: period?.period_label ?? period?.period_code ?? (plan?.fiscal_period_id ? `Period ${plan.fiscal_period_id}` : '—'),
      amendments: committedAmendments,
      repAck:     ackStatus,
      isCurrent:  true,
    }];
  }, [plan, amendments, period]);

  if (!plan) return null;

  const STATUS_STYLE = {
    approved:         'text-[rgb(var(--color-success))]',
    active:           'text-[rgb(var(--color-success))]',
    draft:            'text-[rgb(var(--color-warning))]',
    pending_approval: 'text-[rgb(var(--color-info))]',
    archived:         'text-[rgb(var(--color-text-muted))]',
    rejected:         'text-[rgb(var(--color-danger))]',
  };

  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between px-5 py-4 text-left"
      >
        <div className="flex items-center gap-2">
          <GitBranch className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
          <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Version Matrix</span>
          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">({rows.length} version{rows.length !== 1 ? 's' : ''})</span>
        </div>
        <svg
          className={`h-4 w-4 text-[rgb(var(--color-text-muted))] transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="border-t border-[rgb(var(--color-border))]">
          <div className="overflow-x-auto overflow-y-auto max-h-64">
            <table className="w-full text-tok-sm">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
                  {['Version', 'Status', 'Deployment', 'Amendments', 'Rep Ack', 'Current'].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-2.5 text-left text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--color-border))]">
                {rows.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`${row.isCurrent ? 'bg-[rgb(var(--color-primary))]/3' : 'bg-[rgb(var(--color-surface))]'} hover:bg-[rgb(var(--color-surface-muted))]`}
                  >
                    <td className="px-4 py-3 font-mono text-tok-sm font-medium text-[rgb(var(--color-text))]">
                      v{row.version}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-tok-xs font-medium capitalize ${STATUS_STYLE[row.status] ?? 'text-[rgb(var(--color-text-muted))]'}`}>
                        {String(row.status).replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-tok-sm text-[rgb(var(--color-text))]">
                      {row.deployment}
                    </td>
                    <td className="px-4 py-3 text-center font-mono text-tok-sm text-[rgb(var(--color-text))]">
                      {row.amendments}
                    </td>
                    <td className="px-4 py-3 text-tok-sm text-[rgb(var(--color-text))]">
                      {row.repAck}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {row.isCurrent ? (
                        <CheckCircle className="mx-auto h-4 w-4 text-[rgb(var(--color-success))]" />
                      ) : (
                        <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SoxAuditView — §7 concise governance summary with drill-down
//
// Minimum required fields per spec:
//   Version · Approval · Effective Date · Change Type · Approver · Reason ·
//   Deployment ID · Amendment ID · Acknowledgment Status · Linked Review Artifact
//
// Collapsed by default — auditors open on demand, not during normal admin flow.
// Data sourced entirely from plan + amendments already in state.
// ---------------------------------------------------------------------------

function SoxAuditView({ plan, amendments, period, planId }) {
  const [expanded, setExpanded] = React.useState(false);

  if (!plan) return null;

  const committedAmendments = (amendments ?? []).filter((a) => a.amendment_status === 'committed');
  const correctionAmendments = committedAmendments.filter(
    (a) => a.amendment_reason_code === 'data_correction' || a.amendment_reason_code === 'deployment_override'
  );
  const governedAmendments = committedAmendments.filter(
    (a) => a.amendment_reason_code !== 'data_correction' && a.amendment_reason_code !== 'deployment_override'
  );

  const ackStatus = plan.acknowledged_count != null && plan.assigned_position_count != null
    ? `${plan.acknowledged_count} of ${plan.assigned_position_count} reps`
    : plan.acknowledgment_status
      ? String(plan.acknowledgment_status).replace(/_/g, ' ')
      : '—';

  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
      {/* Header — collapsed by default */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between px-5 py-4 text-left"
      >
        <div className="flex items-center gap-2">
          <svg className="h-4 w-4 text-[rgb(var(--color-text-muted))]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">SOX Audit View</span>
          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Governance summary · Click to expand</span>
        </div>
        <svg
          className={`h-4 w-4 text-[rgb(var(--color-text-muted))] transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="border-t border-[rgb(var(--color-border))] divide-y divide-[rgb(var(--color-border))]">

          {/* Primary governance fields */}
          <div className="grid gap-x-6 gap-y-4 px-5 py-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">Version</p>
              <p className="mt-1 text-tok-sm text-[rgb(var(--color-text))]">
                {plan.version != null ? `v${plan.version}` : '—'}
              </p>
            </div>
            <div>
              <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">Approval Status</p>
              <p className={`mt-1 text-tok-sm font-medium capitalize ${
                plan.plan_status === 'approved' ? 'text-[rgb(var(--color-success))]'
                : plan.plan_status === 'rejected' ? 'text-[rgb(var(--color-danger))]'
                : 'text-[rgb(var(--color-text-muted))]'
              }`}>
                {(plan.plan_status ?? '—').replace(/_/g, ' ')}
              </p>
            </div>
            <div>
              <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">Effective Date</p>
              <p className="mt-1 text-tok-sm text-[rgb(var(--color-text))]">{fmtDate(plan.effective_date)}</p>
            </div>
            <div>
              <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">Approved By</p>
              <p className="mt-1 font-mono text-tok-xs text-[rgb(var(--color-text-muted))] break-all">
                {plan.approved_by ?? '—'}
              </p>
            </div>
            <div>
              <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">Approval Date</p>
              <p className="mt-1 text-tok-sm text-[rgb(var(--color-text))]">{fmtDate(plan.approved_at)}</p>
            </div>
            <div>
              <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">Deployment</p>
              <p className="mt-1 text-tok-sm text-[rgb(var(--color-text))]">
                {period?.period_label ?? period?.period_code ?? (plan.fiscal_period_id ? `Period ${plan.fiscal_period_id}` : '—')}
              </p>
            </div>
            <div>
              <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">Acknowledgment Status</p>
              <p className="mt-1 text-tok-sm text-[rgb(var(--color-text))]">{ackStatus}</p>
            </div>
            <div>
              <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">Plan ID</p>
              <p className="mt-1 font-mono text-tok-xs text-[rgb(var(--color-text-muted))]">{planId ?? '—'}</p>
            </div>
            <div>
              <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">Template</p>
              <p className="mt-1 text-tok-sm text-[rgb(var(--color-text))]">
                {plan.template_name ?? (plan.template_id ? `Template ${plan.template_id}` : '—')}
              </p>
            </div>
          </div>

          {/* Amendment audit rows */}
          {governedAmendments.length > 0 && (
            <div className="px-5 py-4">
              <p className="mb-3 text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                Amendments ({governedAmendments.length})
              </p>
              <div className="overflow-x-auto overflow-y-auto max-h-48 rounded-lg border border-[rgb(var(--color-border))]">
                <table className="w-full text-tok-xs">
                  <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))]">
                    <tr>
                      {['Amendment ID', 'Change Type', 'Reason', 'Date', 'Status'].map((h) => (
                        <th key={h} className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))] border-b border-[rgb(var(--color-border))]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(var(--color-border))]">
                    {governedAmendments.map((a) => (
                      <tr key={a.amendment_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                        <td className="px-3 py-2 font-mono text-[rgb(var(--color-text-muted))]">
                          A-{shortId(a.amendment_id)}
                        </td>
                        <td className="px-3 py-2 text-[rgb(var(--color-text))] capitalize">
                          {(a.amendment_reason_code ?? '—').replace(/_/g, ' ')}
                        </td>
                        <td className="px-3 py-2 text-[rgb(var(--color-text-muted))] max-w-[180px] truncate">
                          {a.amendment_reason_note ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-[rgb(var(--color-text-muted))]">
                          {fmtDate(a.committed_at ?? a.created_at)}
                        </td>
                        <td className="px-3 py-2 capitalize text-[rgb(var(--color-text))]">
                          {a.amendment_status ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Administrative corrections */}
          {correctionAmendments.length > 0 && (
            <div className="px-5 py-4">
              <p className="mb-2 text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                Administrative Corrections ({correctionAmendments.length})
              </p>
              <div className="space-y-1.5">
                {correctionAmendments.map((a) => (
                  <div key={a.amendment_id} className="flex items-center gap-3 text-tok-xs text-[rgb(var(--color-text-muted))]">
                    <span className="font-mono">A-{shortId(a.amendment_id)}</span>
                    <span>·</span>
                    <span className="flex-1 truncate">{a.amendment_reason_note ?? '—'}</span>
                    <span>{fmtDate(a.committed_at ?? a.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Linked review artifact placeholder */}
          <div className="px-5 py-3">
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              <span className="font-medium text-[rgb(var(--color-text))]">Linked Review Artifact:</span>{' '}
              {plan.review_artifact_id
                ? <span className="font-mono">{plan.review_artifact_id}</span>
                : <span className="italic">Not available — generated at deployment review.</span>
              }
            </p>
          </div>

        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// InlineWeightEditor — AD-44 (S684)
// Lightweight inline form for editing weight fields on a draft plan_component.
// Opened when CA clicks a Performance card (Core IC, MBO, NPS, Adoption).
// Self-contained — no PlanBuilder, no wizard, no plan selector.
// PATCH /api/v1/plan-components/{id}/weights — draft components only.
// Gate enforced on backend: approved_by IS NOT NULL → 422, use amendment flow.
// ---------------------------------------------------------------------------

function InlineWeightEditor({ component, focusField, onClose, onSaved }) {
  const [form, setForm] = useState({
    base_pct:        String(component.base_pct        ?? ''),
    variable_pct:    String(component.variable_pct    ?? ''),
    arr_weight:      String(component.arr_weight      ?? ''),
    mbo_weight:      String(component.mbo_weight      ?? ''),
    nps_weight:      String(component.nps_weight      ?? ''),
    adoption_weight: String(component.adoption_weight ?? ''),
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const focusRef            = useRef(null);

  useEffect(() => {
    if (focusRef.current) focusRef.current.focus();
  }, []);

  const FIELD_META = [
    { key: 'base_pct',        label: 'Base %'          },
    { key: 'variable_pct',    label: 'Variable %'       },
    { key: 'arr_weight',      label: 'ARR Weight'       },
    { key: 'mbo_weight',      label: 'MBO Weight'       },
    { key: 'nps_weight',      label: 'NPS Weight'       },
    { key: 'adoption_weight', label: 'Adoption Weight'  },
  ];

  function update(field, val) { setForm((p) => ({ ...p, [field]: val })); }

  async function handleSave() {
    setError('');
    const base  = parseFloat(form.base_pct);
    const vari  = parseFloat(form.variable_pct);
    const arr   = parseFloat(form.arr_weight);
    const mbo   = parseFloat(form.mbo_weight);
    const nps   = parseFloat(form.nps_weight);
    const adop  = parseFloat(form.adoption_weight);

    if ([base, vari, arr, mbo, nps, adop].some(isNaN)) {
      setError('All fields are required.');
      return;
    }
    if (Math.abs(base + vari - 1.0) > 1e-4) {
      setError('Base % + Variable % must equal 1.0');
      return;
    }
    if (Math.abs(arr + mbo + nps + adop - 1.0) > 1e-4) {
      setError('ARR + MBO + NPS + Adoption weights must equal 1.0');
      return;
    }

    setSaving(true);
    try {
      await apiPatch(`/api/v1/plan-components/${component.plan_component_id}/weights`, {
        base_pct:        base,
        variable_pct:    vari,
        arr_weight:      arr,
        mbo_weight:      mbo,
        nps_weight:      nps,
        adoption_weight: adop,
      });
      onSaved();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to save weights. Try again.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-xl border border-[rgb(var(--color-primary))]/20 bg-[rgb(var(--color-surface))] overflow-hidden">
      <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-4 py-3 bg-[rgb(var(--color-surface-muted))]">
        <span className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
          Configure Component Weights
        </span>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-border))]"
          title="Close"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="p-4 flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {FIELD_META.map(({ key, label }) => (
            <label key={key} className="flex flex-col gap-1 text-tok-sm font-medium text-[rgb(var(--color-text))]">
              {label}
              <input
                ref={key === focusField ? focusRef : null}
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={form[key]}
                onChange={(e) => update(key, e.target.value)}
                className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:border-[rgb(var(--color-primary))] focus:outline-none"
              />
            </label>
          ))}
        </div>
        {error && (
          <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
            {error}
          </div>
        )}
        <div className="flex items-center gap-2">
          <button type="button" onClick={handleSave} disabled={saving} className={btnPrimary}>
            {saving ? 'Saving…' : 'Save Weights'}
          </button>
          <button type="button" onClick={onClose} className={btnSecondary}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReviewScreen — S693 mandatory review step before Submit (spec S26.5)
//
// Shows: Plan summary, weights summary, modifier summary with
// configured/unconfigured indicators, validation gate.
// Library path only: delta summary (informational, CA-1, no acknowledgment).
// No data fetching — reads props already in PlanDetailView state.
// ---------------------------------------------------------------------------

function ReviewScreen({
  plan,
  components,
  selectedComponent,
  applicabilityRows,
  roleMap,
  segmentMap,
  buMap,
  planActionError,
  // S698 — rate table gate: true when ARR plan has incomplete/missing rate table
  rateTableBlocked = false,
  // S694 — delta props (PATH A only; undefined/null on PATH B)
  deltaData,
  deltaLoading,
  deltaError,
  // S702 — S29.7 ramp schedule presence for review indicator (non-blocking)
  rampScheduleData = [],
}) {
  const roleName    = roleMap[plan?.role_id]?.role_name    ?? roleMap[plan?.role_id]?.role_code    ?? '—';
  const segmentName = segmentMap[plan?.segment_id]?.segment_name ?? segmentMap[plan?.segment_id]?.segment_code ?? '—';
  const buName      = plan?.business_unit_id ? (buMap[plan.business_unit_id]?.bu_name ?? `BU ${plan.business_unit_id}`) : null;

  const required      = applicabilityRows.filter((r) => r.applicability === 'required');
  const optional      = applicabilityRows.filter((r) => r.applicability === 'optional');
  const configuredReq = required.filter((r) => r.modifier_status === 'approved');
  const allRequiredDone = required.length === 0 || configuredReq.length === required.length;
  // S698 — combined readiness: modifiers + rate table both must be complete
  const fullyReady = allRequiredDone && !rateTableBlocked;

  // Modifier label lookup — reuse same map as PlanModifierChecklist
  const MODIFIER_LABEL = {
    'P3B-CAP': 'Accelerator Cap', 'P3B-KICKER': 'Kicker / Multiplier',
    'P3B-REVTYPE': 'Revenue Type Differentials', 'P3B-CLAWBACK': 'Clawback Policy',
    'P3B-DRAW': 'Draw & Guarantee', 'P3B-SPLIT': 'Deal Splits',
    'P3B-SPIFF': 'SPIFF / Tactical', 'P3B-TXN': 'Transaction Commission',
    'P3B-OVERLAY': 'Overlay Crediting', 'P4-TERRITORY': 'Territory Compensation',
    'P4-MARKET': 'Market Share Incentive', 'P4-PHARMA': 'Script-Based Incentive',
    'P4-VOLUME': 'Volume / Unit Quota', 'P4-RETENTION': 'GRR / NRR Retention',
    'P4-POD': 'Pod / Team', 'P4-RANKING': 'Ranking / Stack Rank',
    'P4-MARGIN': 'Margin / Profit', 'P4-MULTIYEAR': 'Multi-Year Deal',
    'P4-BONUS': 'Shared Bonus Pool', 'P4-CONTEST': 'Contest Programs',
    'P4-NONCASH': 'Non-Cash Incentives', 'P4-TRUEUP': 'Retroactive True-Up',
    'P4-COSELL': 'Co-Sell / Partner', 'P4-CHANNEL': 'Channel / Partner',
  };

  return (
    <div className="flex flex-col gap-5">

      {/* Validation gate banner — S698: checks both modifiers AND rate table */}
      {fullyReady ? (
        <div className="flex items-center gap-3 rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-3">
          <svg className="h-5 w-5 shrink-0 text-[rgb(var(--color-success))]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-tok-sm font-medium text-[rgb(var(--color-success))]">
            Ready to submit — all required modifiers and earnings structure are configured.
          </p>
        </div>
      ) : (
        <div className="flex items-start gap-3 rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 px-4 py-3">
          <svg className="mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--color-warning))]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.963-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div>
            {!allRequiredDone && (
              <p className="text-tok-sm font-medium text-[rgb(var(--color-warning))]">
                {required.length - configuredReq.length} required modifier{required.length - configuredReq.length !== 1 ? 's' : ''} not yet configured
              </p>
            )}
            {rateTableBlocked && (
              <p className="text-tok-sm font-medium text-[rgb(var(--color-warning))]">
                Earnings structure (rate table) is incomplete
              </p>
            )}
            <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-warning))]/80">
              Go back to Configure and complete all required items before submitting.
            </p>
          </div>
        </div>
      )}

      {/* Plan summary */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] divide-y divide-[rgb(var(--color-border))]">
        <div className="px-4 py-3">
          <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">Plan Summary</p>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 px-4 py-3 sm:grid-cols-3">
          <div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Plan Name</p>
            <p className="mt-0.5 text-tok-sm font-medium text-[rgb(var(--color-text))]">{plan?.plan_name ?? '—'}</p>
          </div>
          <div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Plan Code</p>
            <p className="mt-0.5 font-mono text-tok-sm text-[rgb(var(--color-text))]">{plan?.plan_code ?? '—'}</p>
          </div>
          <div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Version</p>
            <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text))]">{plan?.version != null ? `v${plan.version}` : '—'}</p>
          </div>
          <div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Role</p>
            <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text))]">{roleName}</p>
          </div>
          <div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Segment</p>
            <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text))]">{segmentName}</p>
          </div>
          {buName && (
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Business Unit</p>
              <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text))]">{buName}</p>
            </div>
          )}
          {selectedComponent && (
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Components</p>
              <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text))]">{components.length}</p>
            </div>
          )}
        </div>
      </div>

      {/* Weights summary */}
      {selectedComponent && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] divide-y divide-[rgb(var(--color-border))]">
          <div className="px-4 py-3">
            <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">Weights</p>
          </div>
          <div className="grid grid-cols-3 gap-x-4 gap-y-3 px-4 py-3 sm:grid-cols-6">
            {[
              { label: 'Base',     value: selectedComponent.base_pct },
              { label: 'Variable', value: selectedComponent.variable_pct },
              { label: 'ARR',      value: selectedComponent.arr_weight },
              { label: 'MBO',      value: selectedComponent.mbo_weight },
              { label: 'NPS',      value: selectedComponent.nps_weight },
              { label: 'Adoption', value: selectedComponent.adoption_weight },
            ].map(({ label, value }) => (
              <div key={label}>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{label}</p>
                <p className="mt-0.5 text-tok-sm font-medium text-[rgb(var(--color-text))]">
                  {value != null ? `${(Number(value) * 100).toFixed(0)}%` : '—'}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* S698 — Earnings Structure summary (shown when rate table applies) */}
      {!rateTableBlocked !== undefined && applicabilityRows.length > 0 && (() => {
        const _nonArr = new Set(['P3B-TXN','P4-VOLUME','P4-MARKET','P4-PHARMA','P4-POD']);
        const _hasNonArr = applicabilityRows.some(
          (r) => r.applicability === 'required' && _nonArr.has(r.modifier_type)
        );
        if (_hasNonArr) return null;
        const rtComplete = !rateTableBlocked;
        return (
          <div className={`rounded-xl border bg-[rgb(var(--color-surface))] divide-y divide-[rgb(var(--color-border))] ${
            rtComplete
              ? 'border-[rgb(var(--color-border))]'
              : 'border-[rgb(var(--color-warning))]/40'
          }`}>
            <div className="flex items-center justify-between px-4 py-3">
              <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                Earnings Structure
              </p>
              {rtComplete ? (
                <span className="flex items-center gap-1 text-tok-xs font-medium text-[rgb(var(--color-success))]">
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  Complete
                </span>
              ) : (
                <span className="text-tok-xs font-medium text-[rgb(var(--color-warning))]">Incomplete — blocks submission</span>
              )}
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-tok-sm text-[rgb(var(--color-text))]">Rate Table</span>
              {rtComplete ? (
                <span className="flex items-center gap-1 text-tok-xs text-[rgb(var(--color-success))]">
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved and valid
                </span>
              ) : (
                <span className="text-tok-xs text-[rgb(var(--color-warning))]">Not configured — go back to Configure</span>
              )}
            </div>
          </div>
        );
      })()}

      {/* S702 — S29.7 Ramp Schedule presence indicator (non-blocking — amber is informational only) */}
      {selectedComponent?.role_id && (
        <div className={`rounded-xl border bg-[rgb(var(--color-surface))] divide-y divide-[rgb(var(--color-border))] ${
          rampScheduleData.length > 0
            ? 'border-[rgb(var(--color-border))]'
            : 'border-[rgb(var(--color-warning))]/40'
        }`}>
          <div className="flex items-center justify-between px-4 py-3">
            <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
              Ramp Schedule
            </p>
            {rampScheduleData.length > 0 ? (
              <span className="flex items-center gap-1 text-tok-xs font-medium text-[rgb(var(--color-success))]">
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                Configured — {rampScheduleData.length} month{rampScheduleData.length !== 1 ? 's' : ''}
              </span>
            ) : (
              <span className="text-tok-xs font-medium text-[rgb(var(--color-warning))]">
                Not configured — full rate will apply
              </span>
            )}
          </div>
          {rampScheduleData.length > 0 ? (
            <div className="flex items-center gap-1.5 flex-wrap px-4 py-3">
              {rampScheduleData.map((row) => {
                const pct = Math.round((Number(row.ramp_factor) ?? 0) * 100);
                const isFull = pct >= 100;
                return (
                  <span
                    key={row.ramp_month}
                    className={[
                      'inline-flex items-center rounded-full border px-2.5 py-0.5 text-tok-xs font-medium',
                      isFull
                        ? 'border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 text-[rgb(var(--color-success))]'
                        : 'border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]',
                    ].join(' ')}
                  >
                    M{row.ramp_month} · {isFull ? 'Full' : `${pct}%`}
                  </span>
                );
              })}
            </div>
          ) : (
            <div className="px-4 py-3">
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                No ramp schedule is configured for this role. Calculations will apply full attainment.
                Configure in Organisation → Ramp Schedules if this role has new hires.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Modifier summary */}
      {applicabilityRows.length > 0 && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] divide-y divide-[rgb(var(--color-border))]">
          <div className="flex items-center justify-between px-4 py-3">
            <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">Modifiers</p>
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              {configuredReq.length}/{required.length} required configured
            </span>
          </div>
          {required.length > 0 && (
            <div className="px-4 py-3">
              <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] mb-2">Required</p>
              <div className="flex flex-col gap-1.5">
                {required.map((r) => (
                  <div key={r.modifier_type} className="flex items-center justify-between gap-3">
                    <span className="text-tok-sm text-[rgb(var(--color-text))]">
                      {MODIFIER_LABEL[r.modifier_type] ?? r.modifier_type}
                    </span>
                    {r.modifier_status === 'approved' ? (
                      <span className="flex items-center gap-1 text-tok-xs text-[rgb(var(--color-success))]">
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        Approved
                      </span>
                    ) : (
                      <span className="text-tok-xs text-[rgb(var(--color-warning))]">
                        {(r.modifier_status ?? 'not_started').replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {optional.length > 0 && (
            <div className="px-4 py-3">
              <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] mb-2">
                Optional ({optional.filter((r) => r.modifier_status === 'approved').length}/{optional.length} configured)
              </p>
              <div className="flex flex-wrap gap-1.5">
                {optional.map((r) => (
                  <span
                    key={r.modifier_type}
                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-tok-xs ${
                      r.modifier_status === 'approved'
                        ? 'border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 text-[rgb(var(--color-success))]'
                        : 'border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'
                    }`}
                  >
                    {MODIFIER_LABEL[r.modifier_type] ?? r.modifier_type}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* S694 — Template Difference Engine: diff summary for PATH A plans (CA-1, informational).
          plan.template_id present = PATH A (library-deployed). Absent = PATH B (custom).
          deltaData/loading/error come from parent load() — non-blocking fetch.
          TemplateDiffView mode='review': read-only, no acknowledgment at this step.
          Per spec S26.4 and AD-68: one component, two modes. */}
      {plan?.template_id && (
        <TemplateDiffView
          mode="review"
          deltaData={deltaData}
          loading={deltaLoading}
          error={deltaError}
        />
      )}

      {planActionError && (
        <div role="alert" className="rounded-lg border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {planActionError}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SubmitSuccessScreen — S693 post-submit confirmation (spec S26.5)
//
// Eliminates ambiguity about handoff. Shows who needs to act next.
// Renders after successful POST /plans/{id}/submit.
// ---------------------------------------------------------------------------

function SubmitSuccessScreen({ plan, onReturnToPlans, onViewStatus }) {
  return (
    <div className="flex flex-col items-center gap-6 py-8 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[rgb(var(--color-success))]/10 border-2 border-[rgb(var(--color-success))]/30">
        <svg className="h-8 w-8 text-[rgb(var(--color-success))]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>

      <div>
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Submitted Successfully</h2>
        <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
          {plan?.plan_name ?? 'This plan'} has been submitted for approval.
        </p>
      </div>

      <div className="w-full max-w-sm rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-5 py-4">
        <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))] mb-2">
          Waiting For
        </p>
        <p className="text-tok-sm text-[rgb(var(--color-text))]">
          Second Compensation Administrator
        </p>
        <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
          A second CA must approve each plan component in the Approvals tab. Plan status will derive to Approved automatically once all components are approved (AD-42).
        </p>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onReturnToPlans}
          className={
            'rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-medium ' +
            'text-white hover:bg-[rgb(var(--color-primary-hover))]'
          }
        >
          Return to Plans
        </button>
        {onViewStatus && (
          <button
            type="button"
            onClick={onViewStatus}
            className={
              'rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] ' +
              'px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] ' +
              'hover:bg-[rgb(var(--color-surface-muted))]'
            }
          >
            View Status
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PathBEmptyState — Custom path (PATH B) two-step component create + type select
//
// PlanTypeSelector requires an existing plan_component_id (it calls
// POST /plan-components/{id}/set-plan-type). So this wrapper:
//   Step 1: Creates a draft plan_component (POST /plan-components)
//           using plan.role_id + plan.segment_id + sensible defaults.
//   Step 2: Mounts PlanTypeSelector with the new plan_component_id.
//   Step 3: onComplete() → PlanDetailView.load() → grid appears.
// ---------------------------------------------------------------------------

function PathBEmptyState({ plan, planId, period, onComplete }) {
  const [step, setStep]                   = useState('idle'); // idle | creating | select | error
  const [newComponentId, setNewComponentId] = useState(null);
  const [createError, setCreateError]     = useState('');

  async function handleCreateComponent() {
    setStep('creating');
    setCreateError('');
    try {
      const result = await apiPost('/api/v1/plan-components', {
        plan_id:          parseInt(planId, 10),
        role_id:          plan.role_id,
        segment_id:       plan.segment_id,
        base_pct:         0.5,
        variable_pct:     0.5,
        arr_weight:       1.0,
        mbo_weight:       0.0,
        nps_weight:       0.0,
        adoption_weight:  0.0,
        // OTE fields all null — chk_plan_component_ote_all_or_nothing requires all-or-nothing.
        // CA fills OTE in after plan type is set (AD-61).
        ote_min:          null,
        ote_max:          null,
        ote_midpoint:     null,
        ote_currency_code: null
      });
      const cid = result?.plan_component_id ?? result?.data?.plan_component_id;
      if (!cid) throw new Error('Component created but no plan_component_id returned.');
      setNewComponentId(cid);
      setStep('select');
    } catch (err) {
      setCreateError(err?.message || 'Failed to create plan component.');
      setStep('error');
    }
  }

  if (step === 'idle') {
    return (
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-6">
        <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))] mb-1">Select a Plan Type</p>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-4">
          Choose the compensation model for this plan. This determines which modifiers are required, optional, or not applicable.
        </p>
        <button
          type="button"
          onClick={handleCreateComponent}
          className={'rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))]'}
        >
          Choose Plan Type
        </button>
      </div>
    );
  }

  if (step === 'creating') {
    return (
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-6">
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Setting up component…</p>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-6">
        <p className="text-tok-sm text-[rgb(var(--color-danger))] mb-3">{createError}</p>
        <button type="button" onClick={() => setStep('idle')}
          className={'rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm'}>
          Try again
        </button>
      </div>
    );
  }

  // step === 'select'
  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-6">
      <PlanTypeSelector
        planComponentId={newComponentId}
        onComplete={() => onComplete()}
        onCancel={async () => {
          // Delete the draft component we just created so no orphan is left
          try { await fetch(`/api/v1/plan-components/${newComponentId}`, { method: 'DELETE', credentials: 'include' }); } catch (_) {}
          setNewComponentId(null);
          setStep('idle');
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// PlanDetailView — main export
// ---------------------------------------------------------------------------

export default function PlanDetailView({ mode, period, planId: planIdProp }) {
  const { planId: planIdParam, componentId, modifier } = useParams();
  const planId = planIdProp ?? planIdParam;
  const navigate = useNavigate();
  const { user } = useAuth();

  // Core data state
  const [plan,       setPlan]       = useState(null);
  const [components, setComponents] = useState([]);
  const [roleMap,    setRoleMap]    = useState({});
  const [segmentMap, setSegmentMap] = useState({});
  const [buMap,      setBuMap]      = useState({});
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  // Component / modifier navigation
  const [selectedComponentId, setSelectedComponentId] = useState(componentId ?? null);
  const [activeModifier,      setActiveModifier]      = useState(modifier ?? null);
  const [modifierStates,      setModifierStates]      = useState({});
  const [modifierLoading,     setModifierLoading]     = useState(false);
  const [modifierRefreshKey,  setModifierRefreshKey]  = useState(0);
  // Change 2 — applicabilityRows from GET /plan-components/{id}/modifier-applicability
  // Required for PlanTypeModifierGrid to derive correct card states (AD-60 fix).
  const [applicabilityRows,   setApplicabilityRows]   = useState([]);

  // FX
  const [fxPendingCurrencies, setFxPendingCurrencies] = useState(new Set());

  // Amendment state (§7A)
  const [planAmendments,      setPlanAmendments]      = useState([]);
  const [openAmendmentId,     setOpenAmendmentId]     = useState(null);
  const [amendmentReasonCode, setAmendmentReasonCode] = useState(null);
  const [amendmentReasonNote, setAmendmentReasonNote] = useState(null);
  const [committedAmendmentId,setCommittedAmendmentId]= useState(null);
  const [committedStagesCount,setCommittedStagesCount]= useState(0);
  const [showDeltaPanel,      setShowDeltaPanel]      = useState(false);
  // S729 — child entity summary for amendment banner (OI-AMD-07)
  // Populated by GET /api/v1/amendments/{id}/children when amendment is open.
  // spec_amendment_architecture_v2_0.md §7.4
  const [amendmentChildren,   setAmendmentChildren]   = useState([]);

  // Actions dropdown + amendment flow state
  const [showAddModifierPicker,   setShowAddModifierPicker]   = useState(false);
  const [showStageImpactPreview,  setShowStageImpactPreview]  = useState(false);
  const [stageImpactMap,          setStageImpactMap]          = useState([]);
  const [stageImpactLoading,      setStageImpactLoading]      = useState(false);
  const [stageImpactError,        setStageImpactError]        = useState('');
  const [showAmendmentModal,      setShowAmendmentModal]      = useState(false);
  const [showCommitConfirm,       setShowCommitConfirm]       = useState(false);
  const [committing,              setCommitting]              = useState(false);
  const [commitError,             setCommitError]             = useState('');
  const [cancelling,              setCancelling]              = useState(false);
  const [cancelError,             setCancelError]             = useState('');

  // Administrative Correction modal
  const [showCorrectionModal,  setShowCorrectionModal]  = useState(false);
  const [correctionDesc,       setCorrectionDesc]       = useState('');
  const [correctionError,      setCorrectionError]      = useState('');
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);

  // Toast
  const [toast, setToast] = useState('');

  // AD-44 — Inline PlanBuilder for weight card clicks (S684)
  const [showInlinePlanBuilder,       setShowInlinePlanBuilder]       = useState(false);
  const [inlinePlanBuilderFocusField, setInlinePlanBuilderFocusField] = useState(null);

  // §9 — Save as Template wizard state
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false);

  // S693 — PlanWizardShell step state
  // Wizard is active only when mode === 'wizard'. In 'admin' mode, PlanDetailView
  // renders its existing standalone layout (no shell, no step indicator).
  // PATH B steps: 0=Plan Type, 1=Plan Details, 2=Configure, 3=Review, 4=Submit
  // PATH A steps: 0=Source, 1=Plan Details, 2=Configure, 3=Review, 4=Submit
  const [wizardStep, setWizardStep] = useState(0);
  const [showSubmitSuccess, setShowSubmitSuccess] = useState(false);

  // GAP-UNSAVED-PROTECTION (S695): tracks which modifier is expanded in PlanModifierChecklist.
  // When non-null, wizard Back/Next warns before navigating away — the CA may have
  // unsaved work in the open modifier manager. Warning only — never blocks navigation.
  // Reset to null when wizard step changes (manager unmounts on step change anyway).
  const [activeExpandedModifier, setActiveExpandedModifier] = useState(null);

  // S697 — Rate table completion state per plan component (S28.11.4)
  // Keyed by plan_component_id (string). RateTableManager emits onComplete(bool)
  // whenever its validation state changes. Submit is blocked until all applicable
  // components have rateTableComplete=true.
  const [rateTableComplete, setRateTableComplete] = useState({});

  // S694 — Template Difference Engine state
  // deltaData: response from GET /api/v1/template-library/deltas/by-plan/{plan_id}
  // Fetched non-blocking inside load() when planObj.template_id is set (PATH A only).
  // PATH B plans (no template_id) never trigger this fetch — deltaData stays null.
  const [deltaData,    setDeltaData]    = useState(null);
  const [deltaLoading, setDeltaLoading] = useState(false);
  const [deltaError,   setDeltaError]   = useState(null);

  // S702 — S29.7 Ramp Schedule read-only display (AD-86 secondary surface)
  // Fetched on component selection by role_id. Most recent effective_date group only.
  // Non-blocking — empty array on error or no data. Never affects submit gate.
  const [rampScheduleData,    setRampScheduleData]    = useState([]);
  const [rampScheduleLoading, setRampScheduleLoading] = useState(false);

  // Plan governance — Submit only (AD-42: plan approval is DERIVED from
  // component approval state via _derive_plan_status(). Direct plan-level approve/reject
  // from UI is removed — GAP-PDV-06. CA-2 approves plan_components via the Approvals tab.)
  const [planActionLoading, setPlanActionLoading] = useState(false);
  const [planActionError,   setPlanActionError]   = useState('');

  // CHANGE-09 — Plan Readiness panel state (S724)
  // GET /api/v1/compensation/plans/{plan_id}/readiness
  // Confirmed shape: { overall_status: "ready"|"blocked"|"warning", checks: [{check_key, label, status: "pass"|"fail"|"warning", detail}] }
  // Non-blocking — panel simply does not render on error (silent-failure rule per spec Part 3 CHANGE-09)
  const [readiness,        setReadiness]        = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }

  // Call after ANY modifier lifecycle mutation — create, submit, approve, return, cancel.
  // Global invalidation is intentional: modifier states are interdependent (P0.3).
  // Stale values remain visible during refresh — no flash to blank.
  // S729: also refreshes amendment children so banner stays current.
  function invalidateModifierCache() {
    setModifierStates({});
    setApplicabilityRows([]);
    setModifierRefreshKey((k) => k + 1);
    if (openAmendmentId) fetchAmendmentChildren(openAmendmentId);
  }

  // ---------------------------------------------------------------------------
  // Load plan data
  // ---------------------------------------------------------------------------

  const load = useCallback(async () => {
    if (!user || !planId) { setLoading(false); return; }  // OI-PLAN-DETAIL-BLANK-ROUTE S644: ensure loading=false on bail
    setLoading(true);
    setError('');
    try {
      const periodId = period?.fiscal_period_id;
      // Change 11 — scope component fetch to plan_id. Previously fetched all components
      // and filtered client-side, which returns stale/incorrect modifier state for the grid.
      const compUrl = periodId
        ? `/api/v1/plan-components?plan_id=${planId}&fiscal_period_id=${periodId}`
        : `/api/v1/plan-components?plan_id=${planId}`;
      const [planData, compData, rolesData, segsData, busData] = await Promise.all([
        apiGet(`/api/v1/plans/${planId}`),
        apiGet(compUrl),
        apiGet('/api/v1/roles'),
        apiGet('/api/v1/segments'),
        apiGet('/api/v1/business-units'),
      ]);

      const planObj   = planData?.data ?? planData;
      const allComps  = Array.isArray(compData) ? compData : compData?.data ?? compData?.plan_components ?? [];
      // plan_id filter applied server-side; client-side filter retained as safety guard
      const planComps = allComps.filter((c) => String(c.plan_id) === String(planId));

      const roles = Array.isArray(rolesData) ? rolesData : rolesData?.data ?? [];
      const segs  = Array.isArray(segsData)  ? segsData  : segsData?.data  ?? [];
      const bus   = Array.isArray(busData)   ? busData   : busData?.data   ?? [];

      setPlan(planObj);
      setComponents(planComps);
      setRoleMap(Object.fromEntries(roles.map((r) => [r.role_id, r])));
      setSegmentMap(Object.fromEntries(segs.map((s) => [s.segment_id, s])));
      setBuMap(Object.fromEntries(bus.map((b) => [b.business_unit_id, b])));

      if (!selectedComponentId && planComps.length > 0) {
        setSelectedComponentId(planComps[0].plan_component_id);
      }

      if (periodId) {
        try {
          const fxData = await apiGet(`/api/v1/exchange-rates?is_locked=false&fiscal_period_id=${periodId}`);
          const fxList = Array.isArray(fxData) ? fxData : fxData?.data ?? fxData?.exchange_rates ?? [];
          const pending = new Set(fxList.flatMap((fx) => [fx.from_currency, fx.to_currency].filter(Boolean)));
          setFxPendingCurrencies(pending);
        } catch (_) { /* non-blocking */ }
      }

      try {
        const amendData = await apiGet(`/api/v1/amendments?fiscal_period_id=${periodId ?? ''}`);
        const allAmends = Array.isArray(amendData) ? amendData : amendData?.data ?? amendData?.amendments ?? [];
        const componentIds = new Set(planComps.map((c) => String(c.plan_component_id)));
        const planAmends = allAmends.filter(
          (a) => a.amended_entity_type === 'plan_component' && componentIds.has(String(a.entity_id ?? a.amended_entity_id))
        );
        setPlanAmendments(planAmends);

        const openAmend = planAmends.find((a) => a.amendment_status === 'open');
        if (openAmend && !openAmendmentId) {
          setOpenAmendmentId(openAmend.amendment_id);
          setAmendmentReasonCode(openAmend.amendment_reason_code);
          setAmendmentReasonNote(openAmend.amendment_reason_note ?? null);
        }
      } catch (_) { /* non-blocking */ }

      // S694 — Template Difference Engine: fetch delta for PATH A plans only.
      // planObj.template_id is the local variable (not state) — available immediately
      // after the apiGet above. Non-blocking: delta unavailable never blocks plan load.
      if (planObj.template_id) {
        setDeltaLoading(true);
        setDeltaError(null);
        try {
          const deltaResp = await apiGet(
            `/api/v1/template-library/deltas/by-plan/${planId}`
          );
          setDeltaData(deltaResp);
        } catch (deltaErr) {
          // 404 = no delta record yet (plan deployed but review not yet triggered) — not an error
          if (deltaErr?.status === 404 || deltaErr?.response?.status === 404) {
            setDeltaData(null);
            setDeltaError(null);
          } else {
            setDeltaError('Unable to load template comparison.');
            setDeltaData(null);
          }
        } finally {
          setDeltaLoading(false);
        }
      } else {
        // PATH B plan — clear any stale delta state
        setDeltaData(null);
        setDeltaError(null);
        setDeltaLoading(false);
      }

      // CHANGE-09 — Readiness panel: non-blocking fetch. Failure = panel does not render.
      setReadinessLoading(true);
      try {
        const readinessData = await apiGet(`/api/v1/compensation/plans/${planId}/readiness`);
        setReadiness(readinessData);
      } catch (_) {
        // Silent failure per spec — retain last known state rather than clearing
      } finally {
        setReadinessLoading(false);
      }

    } catch (err) {
      setError(getErrorMessage(err, 'Unable to load plan.'));
    } finally {
      setLoading(false);
    }
  }, [user, planId, period]); // eslint-disable-line

  useEffect(() => { load(); }, [user, planId, period?.fiscal_period_id]); // eslint-disable-line

  // ---------------------------------------------------------------------------
  // Load modifier states for selected component
  // ---------------------------------------------------------------------------

  // Change 2 — Fetch modifier-applicability rows when selected component changes.
  // These rows feed PlanTypeModifierGrid.deriveCardState (AD-60 fix).
  // Non-blocking: grid shows not_started on error, which is safe.
  useEffect(() => {
    if (!selectedComponentId) {
      setApplicabilityRows([]);
      return;
    }
    apiGet(`/api/v1/plan-components/${selectedComponentId}/modifier-applicability`)
      .then((data) => {
        const rows = Array.isArray(data) ? data : data?.modifiers ?? data?.applicability_rows ?? [];
        setApplicabilityRows(rows);
      })
      .catch(() => setApplicabilityRows([]));
  }, [selectedComponentId]); // eslint-disable-line

  // S702 — S29.7: Fetch ramp schedule for the selected component's role_id.
  // Fires whenever selectedComponentId changes. role_id derived from components
  // array (already in state) — avoids temporal dead zone with selectedComponent
  // useMemo which is declared later in the function body.
  // Filters client-side to the single most recent effective_date group — all months
  // for that date. Non-blocking: display shows "not configured" note on error/empty.
  useEffect(() => {
    const comp = components.find(
      (c) => String(c.plan_component_id) === String(selectedComponentId)
    );
    const roleId = comp?.role_id;
    if (!roleId) {
      setRampScheduleData([]);
      return;
    }
    setRampScheduleLoading(true);
    apiGet(`/api/v1/ramp-schedules?role_id=${roleId}`)
      .then((data) => {
        const rows = Array.isArray(data) ? data : data?.data ?? [];
        if (rows.length === 0) {
          setRampScheduleData([]);
          return;
        }
        // Most recent effective_date only — sort descending, take the top date group
        const sorted = [...rows].sort(
          (a, b) => new Date(b.effective_date) - new Date(a.effective_date)
        );
        const mostRecentDate = sorted[0].effective_date;
        const mostRecentGroup = sorted
          .filter((r) => r.effective_date === mostRecentDate)
          .sort((a, b) => a.ramp_month - b.ramp_month);
        setRampScheduleData(mostRecentGroup);
      })
      .catch(() => setRampScheduleData([]))
      .finally(() => setRampScheduleLoading(false));
  }, [selectedComponentId, components]); // eslint-disable-line

  // OI-PDV-PERF-01 S733 — Replace 12-endpoint modifier state waterfall with PCMA data.
  //
  // Previously: Promise.allSettled of 12 parallel apiGet calls per component selection.
  // 12 HTTP requests × N component switches per session = the primary cause of slow load.
  //
  // Now: derive modifier states from applicabilityRows (already in state, fetched by the
  // useEffect above via GET /plan-components/{id}/modifier-applicability). One round trip,
  // already in flight. Zero additional HTTP requests.
  //
  // PCMA modifier_status vocabulary matches deriveModifierState output exactly:
  //   not_started | draft | pending_approval | approved
  //
  // Modifiers not in PCMA (no set-plan-type called yet) → not_started (safe default).
  // caps (accelerator caps) → derived from max_period_payout on the component row,
  // same as before — caps don't have a PCMA row (P3B-CAP has no modifier_entity table).
  //
  // Runs whenever applicabilityRows or selectedComponentId changes — applicabilityRows
  // already updates when selectedComponentId changes (useEffect above).

  // PCMA modifier_type → PlanDetailView card key mapping
  const _PCMA_TYPE_TO_CARD_KEY = {
    'P3B-TXN':     'transaction',
    'P3B-DRAW':    'draw',
    'P3B-SPLIT':   'deal-split',
    'P3B-SPIFF':   'spiff',
    'P3B-REVTYPE': 'rev-rates',
    'P3B-KICKER':  'kicker',
    'P3B-CLAWBACK':'clawback',
    'P4-MARGIN':   'margin',
    'P4-RETENTION':'retention',
    'P4-VOLUME':   'volume',
    'P4-MULTIYEAR':'multi-year',
    'P4-RANKING':  'ranking',
    'P4-TERRITORY':'territory',
    'P4-OVERLAY':  'overlay',
    'P4-COSELL':   'cosell',
    'P4-TRUEUP':   'trueup',
    'P4-POD':      'pod',
    'P4-CONTEST':  'contest',
    'P4-BONUS':    'bonuspool',
    'P4-NONCASH':  'noncash',
    'P4-MARKET':   'marketshare',
    'P4-PHARMA':   'pharma',
    'P4-CHANNEL':  'channel',
  };

  useEffect(() => {
    if (!selectedComponentId) return;
    const cid = String(selectedComponentId);

    setModifierLoading(true);

    // Build state map from PCMA rows already in state — zero HTTP requests
    const stateMap = {};
    for (const row of applicabilityRows) {
      const cardKey = _PCMA_TYPE_TO_CARD_KEY[row.modifier_type];
      if (cardKey) {
        // not_applicable rows map to not_started — they're hidden from the grid anyway
        stateMap[cardKey] = row.applicability === 'not_applicable'
          ? 'not_started'
          : (row.modifier_status ?? 'not_started');
      }
    }

    // caps: derived from max_period_payout on component row — no PCMA entry
    stateMap['caps'] = components.find((c) => String(c.plan_component_id) === cid)?.max_period_payout
      ? 'approved'
      : 'not_started';

    setModifierStates((prev) => ({ ...prev, [cid]: stateMap }));
    setModifierLoading(false);
  }, [selectedComponentId, applicabilityRows, components, modifierRefreshKey]); // eslint-disable-line

  // ---------------------------------------------------------------------------
  // Sync URL params → local state
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (componentId) setSelectedComponentId(componentId);
    if (modifier)    setActiveModifier(modifier);
  }, [componentId, modifier]);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const selectedComponent = useMemo(
    () => components.find((c) => String(c.plan_component_id) === String(selectedComponentId)) ?? components[0] ?? null,
    [components, selectedComponentId]
  );

  const oteCurrencies = useMemo(() => {
    const codes = [...new Set(components.map((c) => c.ote_currency_code).filter(Boolean))];
    return codes;
  }, [components]);

  const tenantReportingCurrency = 'USD';

  const showCurrencySubheader = oteCurrencies.length > 0 &&
    !(oteCurrencies.length === 1 && oteCurrencies[0] === tenantReportingCurrency);

  const currentModifierStates = selectedComponentId
    ? (modifierStates[String(selectedComponentId)] ?? {})
    : {};

  function cardHasFxWarning(card) {
    if (!card.hasFx) return false;
    const compCurrency = selectedComponent?.ote_currency_code;
    if (!compCurrency) return false;
    return fxPendingCurrencies.has(compCurrency);
  }

  const hasOpenAmendment = !!openAmendmentId;
  const wizardSessionId  = null;

  const assignedPositionCount = plan?.assigned_position_count ?? null;
  const periodLabel = period?.period_name ?? period?.fiscal_year ?? null;

  // ---------------------------------------------------------------------------
  // Handlers — component / modifier navigation
  // ---------------------------------------------------------------------------

  function handleComponentSelect(cid) {
    setSelectedComponentId(cid);
    setActiveModifier(null);
    if (!planIdProp) navigate(`/admin/plans/${planId}/components/${cid}`, { replace: true });
  }

  function handleModifierClick(card) {
    if (hasOpenAmendment) { /* amendment open — allow editing (§7A Step 4) */ }
    if (activeModifier === card.path) {
      setActiveModifier(null);
      if (!planIdProp) navigate(`/admin/plans/${planId}/components/${selectedComponentId}`, { replace: true });
    } else {
      setActiveModifier(card.path);
      if (!planIdProp) navigate(`/admin/plans/${planId}/components/${selectedComponentId}/${card.path}`, { replace: true });
    }
  }

  // ---------------------------------------------------------------------------
  // Handlers — Actions dropdown + Add Modifier picker (CHANGE-04/05 / S724)
  // ---------------------------------------------------------------------------

  // CHANGE-13 Part A2 S737 — P0 governance fix (L-130, Conflict #2).
  // handleAddModifier previously routed approved-plan Add Modifier through
  // fetchStageImpactAndShowPreview() — the full amendment begin flow. This violated AD-169:
  // Add Modifier on an approved plan does NOT open an amendment. New modifier has its own
  // lifecycle (AD-169 vs AD-192 — these are orthogonal, Rule 20). Open picker directly.
  function handleAddModifier() {
    setShowAddModifierPicker(true);
  }

  // CHANGE-13 Part E S737 — Propose Change + Start Change entry points.
  // Called from PlanConfigSummary [Propose Change] buttons (per-section amendment begin).
  // S737 scope: both route to fetchStageImpactAndShowPreview() — existing amendment begin flow.
  // Per-section entity scoping is T2-C (future session).
  function handleProposeChange() {
    fetchStageImpactAndShowPreview();
  }

  function handleStartChange() {
    fetchStageImpactAndShowPreview();
  }

  function handleModifierTypeSelected(modifierKey) {
    setShowAddModifierPicker(false);
    const card = MODIFIER_CARDS.find((c) => c.key === modifierKey);
    if (!card) {
      showToast('Modifier type not yet available in this workspace.');
      return;
    }
    handleModifierClick(card);
  }

  async function handleCreateChangeContinue(path) {

    if (path === 'template') {
      const templateId = plan?.template_id ?? null;
      if (!templateId) {
        showToast('Template ID not available on this plan. Contact your administrator.');
        return;
      }
      try {
        await apiPost(`/api/v1/template-library/templates/${templateId}/versions`, {
          source_plan_id: planId,
        });
        showToast('New template version draft created. Proceed to the Template Library to complete.');
        await load();
      } catch (err) {
        showToast(getErrorMessage(err, 'Failed to initiate template version. Try again.'));
      }
      return;
    }

    if (path === 'amendment') {
      await fetchStageImpactAndShowPreview();
      return;
    }

    if (path === 'correction') {
      setCorrectionDesc('');
      setCorrectionError('');
      setShowCorrectionModal(true);
    }
  }

  async function fetchStageImpactAndShowPreview() {
    setStageImpactLoading(true);
    setStageImpactError('');
    setStageImpactMap([]);
    setShowStageImpactPreview(true);
    try {
      const data = await apiGet('/api/v1/amendment-stage-impact-map');
      const entries = Array.isArray(data) ? data : data?.data ?? data?.stages ?? [];
      setStageImpactMap(entries);
    } catch (err) {
      setStageImpactError(getErrorMessage(err, 'Unable to load stage impact map.'));
    } finally {
      setStageImpactLoading(false);
    }
  }

  async function handleStageImpactBegin() {
    // S549 §10: call impact-preview before opening AmendmentBeginModal to enrich
    // stageImpactMap with plan-scoped stage list and assigned_position_count.
    // Non-blocking — falls back to existing stageImpactMap from GET /api/v1/amendment-stage-impact-map.
    if (selectedComponent && planId && period?.fiscal_period_id) {
      try {
        const preview = await apiPost(`/api/v1/compensation/plans/${planId}/impact-preview`, {
          plan_component_id:     selectedComponent.plan_component_id,
          fiscal_period_id:      period.fiscal_period_id,
          amendment_reason_code: amendmentReasonCode ?? 'plan_design_fix',
        });
        if (Array.isArray(preview?.stages_invalidated) && preview.stages_invalidated.length > 0) {
          setStageImpactMap(preview.stages_invalidated);
        }
        if (preview?.assigned_position_count != null) {
          setPlan((prev) => prev ? { ...prev, assigned_position_count: preview.assigned_position_count } : prev);
        }
      } catch (_) {
        // Non-blocking — existing stageImpactMap remains
      }
    }
    setShowStageImpactPreview(false);
    setShowAmendmentModal(true);
  }

  function handleStageImpactCancel() {
    setShowStageImpactPreview(false);
    setStageImpactMap([]);
  }

  function handleAmendmentBegun(amendmentId, reasonCode, reasonNote) {
    setShowAmendmentModal(false);
    setOpenAmendmentId(amendmentId);
    setAmendmentReasonCode(reasonCode ?? null);
    setAmendmentReasonNote(reasonNote ?? null);
    setCommittedAmendmentId(null);
    setShowDeltaPanel(false);
    setAmendmentChildren([]);
    load();
  }

  // S729 — Fetch child entities registered for the open amendment.
  // Called after modifier creation mutations to keep the banner current.
  // Non-blocking — banner simply shows no children if this fails.
  // spec_amendment_architecture_v2_0.md §7.4
  async function fetchAmendmentChildren(amendmentId) {
    if (!amendmentId) { setAmendmentChildren([]); return; }
    try {
      const data = await apiGet(`/api/v1/amendments/${amendmentId}/children`);
      setAmendmentChildren(data?.children ?? []);
    } catch (_) {
      // Non-blocking — banner degrades gracefully
      setAmendmentChildren([]);
    }
  }

  // ---------------------------------------------------------------------------
  // Handlers — Commit amendment (§7A Step 5)
  // ---------------------------------------------------------------------------

  function handleCommitClick() { setCommitError(''); setShowCommitConfirm(true); }

  async function handleCommitConfirm() {
    if (!openAmendmentId) return;
    setCommitting(true);
    setCommitError('');
    try {
      await apiPost(`/api/v1/amendments/${openAmendmentId}/commit`, { surface: 'comp_policies' });
      const prevId = openAmendmentId;
      const affectedCount = stageImpactMap.filter(
        (e) => e.amended_entity_type === 'plan_component' || e.entity_type === 'plan_component'
      ).length || 3;
      setOpenAmendmentId(null);
      setAmendmentReasonCode(null);
      setAmendmentReasonNote(null);
      setCommittedAmendmentId(prevId);
      setCommittedStagesCount(affectedCount);
      setShowCommitConfirm(false);
      await load();
    } catch (err) {
      setCommitError(getErrorMessage(err, 'Failed to commit amendment. Try again.'));
    } finally {
      setCommitting(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Handlers — Cancel amendment
  // ---------------------------------------------------------------------------

  async function handleCancelAmendment() {
    if (!openAmendmentId) return;
    setCancelling(true);
    setCancelError('');
    try {
      await apiPost(`/api/v1/amendments/${openAmendmentId}/cancel`, {});
      setOpenAmendmentId(null);
      setAmendmentReasonCode(null);
      setAmendmentReasonNote(null);
      await load();
    } catch (err) {
      setCancelError(getErrorMessage(err, 'Failed to cancel amendment.'));
    } finally {
      setCancelling(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Handlers — Administrative Correction
  // ---------------------------------------------------------------------------

  async function handleCorrectionSubmit() {
    if (!correctionDesc.trim()) { setCorrectionError('Description is required.'); return; }
    if (!selectedComponent) { setCorrectionError('No component selected.'); return; }
    setCorrectionSubmitting(true);
    setCorrectionError('');
    try {
      const result = await apiPost('/api/v1/amendments/begin', {
        amended_entity_type:   'plan_component',
        entity_id:             String(selectedComponent.plan_component_id),
        amendment_reason_code: 'data_correction',
        amendment_reason_note: correctionDesc.trim(),
        surface:               'comp_policies',
        fiscal_period_id:      period?.fiscal_period_id ?? null,
      });
      const amendmentId = result?.amendment_id ?? result?.data?.amendment_id;
      await apiPost(`/api/v1/amendments/${amendmentId}/commit`, { surface: 'comp_policies' });
      setShowCorrectionModal(false);
      setCorrectionDesc('');
      showToast('Administrative correction recorded.');
      await load();
    } catch (err) {
      setCorrectionError(getErrorMessage(err, 'Failed to record administrative correction. Try again.'));
    } finally {
      setCorrectionSubmitting(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Plan governance handlers — Submit / Approve / Reject
  // ---------------------------------------------------------------------------

  async function handlePlanSubmit() {
    setPlanActionLoading(true);
    setPlanActionError('');
    try {
      await apiPost(`/api/v1/plans/${planId}/submit`, {});
      showToast('Plan submitted for approval.');
      await load();
    } catch (err) {
      setPlanActionError(getErrorMessage(err, 'Failed to submit plan.'));
    } finally {
      setPlanActionLoading(false);
    }
  }

  // CHANGE-06 — Withdraw Submission (S725 — backend endpoint now exists)
  // Only the original submitter may withdraw (plan.submitted_by === user.user_account_id).
  // POST /api/v1/plans/{id}/withdraw-submission — returns plan to draft.
  async function handleWithdrawSubmission() {
    setPlanActionLoading(true);
    setPlanActionError('');
    try {
      await apiPost(`/api/v1/plans/${planId}/withdraw-submission`, {});
      showToast('Submission withdrawn. Plan returned to draft.');
      await load();
    } catch (err) {
      setPlanActionError(getErrorMessage(err, 'Failed to withdraw submission.'));
    } finally {
      setPlanActionLoading(false);
    }
  }

  // GAP-PDV-06 / AD-42: handlePlanApprove() and handlePlanReject() removed.
  // Plan status is DERIVED by _derive_plan_status() in plans_router.py when
  // ALL plan_components have approved_by IS NOT NULL. Direct POST /plans/{id}/approve
  // or /reject from UI violates the governance model. CA-2 approves individual
  // plan_components via the Approvals tab — plan status derives automatically.

  // ---------------------------------------------------------------------------
  // §9 — Save as Template handlers
  // ---------------------------------------------------------------------------

  function handleSaveAsTemplateClick() {
    setShowSaveAsTemplate(true);
  }

  function handleSaveAsTemplateClose() {
    setShowSaveAsTemplate(false);
  }

  function handleTemplateCreated(data) {
    // §9 success: toast with link to Template Library
    const templateId = data?.template_id ?? null;
    setShowSaveAsTemplate(false);
    showToast(
      templateId
        ? 'Template draft created. Submit for approval in the Template Library.'
        : 'Template draft created. Submit for approval in the Template Library.'
    );
    // Re-load plan in case server updated any fields
    load();
  }

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <section className="flex flex-col gap-4 animate-pulse">
        <div className="h-16 rounded-xl bg-[rgb(var(--color-surface-muted))]" />
        <div className="h-8 rounded-xl bg-[rgb(var(--color-surface-muted))] w-1/3" />
        <div className="h-10 rounded-xl bg-[rgb(var(--color-surface-muted))]" />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[1,2,3,4,5,6,7,8].map((i) => (
            <div key={i} className="h-24 rounded-xl bg-[rgb(var(--color-surface-muted))]" />
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="flex flex-col gap-4">
        {!planIdProp && (
          <button type="button" onClick={() => navigate('/admin/plans')} className={btnSecondary + ' w-fit'}>
            ← Back to Plans
          </button>
        )}
        <div className="rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {error}
        </div>
      </section>
    );
  }

  // OI-PLAN-DETAIL-BLANK-ROUTE S644: render informative state instead of blank when plan is null post-load
  if (!plan) return (
    <section className="flex flex-col gap-4">
      {!planIdProp && (
        <button type="button" onClick={() => navigate('/admin/plans')} className={btnSecondary + ' w-fit'}>
          ← Back to Plans
        </button>
      )}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
        Plan not found. It may have been removed or you may not have access.
      </div>
    </section>
  );

  const planStatus  = plan.plan_status ?? 'draft';
  const statusStyle = PLAN_STATUS_STYLE[planStatus] ?? PLAN_STATUS_STYLE.draft;
  const isApproved  = planStatus === 'approved';
  const isArchived  = planStatus === 'archived';
  const isReadOnly  = (isApproved && !hasOpenAmendment) || isArchived;

  const roleName    = roleMap[plan.role_id]?.role_name    ?? roleMap[plan.role_id]?.role_code    ?? null;
  const segmentName = segmentMap[plan.segment_id]?.segment_name ?? segmentMap[plan.segment_id]?.segment_code ?? null;
  const buName      = plan.business_unit_id ? (buMap[plan.business_unit_id]?.bu_name ?? `BU ${plan.business_unit_id}`) : null;

  const ActiveManager = activeModifier ? MANAGER_MAP[activeModifier] : null;

  const amendmentReasonLabel = AMENDMENT_REASON_LABELS[amendmentReasonCode] ?? amendmentReasonCode ?? '—';

  // CHANGE-17 S737 — Save as Template gate fix (C-1 P0 — confirmed live bug, L-125).
  // gate was missing assignment_count > 0 check at lines 3221–3222.
  // Correct field: plan?.assignment_count (from GET /plans/{id} line 482 — NOT assigned_position_count).
  // Spec S31.8: gate = plan_status === 'approved' AND assignment_count > 0.
  // SME rationale: "premature before the plan has proved itself in production via assignment."
  // showSaveAsTemplateButton: visible whenever approved — button always shown (grayed when not yet assigned).
  // enableSaveAsTemplateButton: enabled only when approved + no open amendment + assigned.
  const showSaveAsTemplateButton   = isApproved;
  const enableSaveAsTemplateButton = isApproved && !hasOpenAmendment && (plan?.assignment_count ?? 0) > 0;

  // CHANGE-12 S737 — PlanConfigSummary shown for approved and pending_approval plans (AD-198).
  // Draft plans: modifier grid remains primary — no change.
  const showPlanConfigSummary = isApproved || planStatus === 'pending_approval';

  // S693 — Wizard mode derived values
  // Determine PATH A vs PATH B from plan.template_id presence.
  // Library-deployed plans carry template_id; custom plans do not.
  const isWizardMode = mode === 'wizard';
  const isPathA      = isWizardMode && !!plan.template_id;
  const wizardSteps  = isPathA ? WIZARD_STEPS_PATH_A : WIZARD_STEPS_PATH_B;

  // Progress meter: feed from applicabilityRows (already in state)
  const requiredRows     = applicabilityRows.filter((r) => r.applicability === 'required');
  const configuredCount  = requiredRows.filter((r) => r.modifier_status === 'approved').length;
  const wizardProgress   = requiredRows.length > 0
    ? { configured: configuredCount, total: requiredRows.length, label: 'required configured' }
    : null;

  // Submit button disabled when required modifiers incomplete
  const submitBlocked = requiredRows.length > 0 && configuredCount < requiredRows.length;

  // S697 — Rate table submit gate (S28.15)
  // Same detection logic as configure step: block unless non-ARR primary is required.
  const _SUBMIT_NON_ARR_TYPES = new Set(['P3B-TXN', 'P4-VOLUME', 'P4-MARKET', 'P4-PHARMA', 'P4-POD']);
  const _submitHasNonArrPrimary = applicabilityRows.some(
    (r) => r.applicability === 'required' && _SUBMIT_NON_ARR_TYPES.has(r.modifier_type),
  );
  const _selectedComponentNeedsRateTable = applicabilityRows.length > 0 && !_submitHasNonArrPrimary;
  const rateTableSubmitBlocked =
    _selectedComponentNeedsRateTable &&
    rateTableComplete[String(selectedComponent?.plan_component_id)] !== true;

  // Wizard step key for current step
  const currentStepKey = wizardSteps[wizardStep]?.key ?? '';

  // GAP-UNSAVED-PROTECTION (S695): called by PlanModifierChecklist when a modifier expands
  // or collapses. PlanDetailView tracks this so wizard nav can warn before leaving the
  // Configure step with an open manager. onExpandedChange(null) = all collapsed.
  function handleModifierExpandedChange(modifierType) {
    setActiveExpandedModifier(modifierType);
  }

  // S697 — Rate table completion callback (S28.11.4)
  // Called by RateTableManager whenever its isComplete state changes.
  // Stores completion per component so switching components preserves prior state.
  function handleRateTableCompletion(isComplete) {
    if (!selectedComponent) return;
    const cid = String(selectedComponent.plan_component_id);
    setRateTableComplete((prev) => {
      if (prev[cid] === isComplete) return prev;
      return { ...prev, [cid]: isComplete };
    });
  }

  // S693 — Wizard navigation handlers
  // GAP-UNSAVED-PROTECTION: warn (window.confirm) when a modifier is expanded and
  // the CA tries to navigate away. Warning only — never blocks navigation per spec S695 S4.
  // CA can choose to continue and discard unsaved work, or cancel and save first.
  function handleWizardBack() {
    if (activeExpandedModifier) {
      const proceed = window.confirm(
        'You have a modifier open that may have unsaved changes.\n\n' +
        'Going back will leave this step — any unsaved changes in the open modifier ' +
        'will be lost.\n\nContinue anyway?'
      );
      if (!proceed) return;
    }
    setActiveExpandedModifier(null);
    setWizardStep((s) => Math.max(0, s - 1));
  }

  function handleWizardNext() {
    if (activeExpandedModifier) {
      const proceed = window.confirm(
        'You have a modifier open that may have unsaved changes.\n\n' +
        'Moving to the next step will leave this step — any unsaved changes in the ' +
        'open modifier will be lost.\n\nContinue anyway?'
      );
      if (!proceed) return;
    }
    setActiveExpandedModifier(null);
    setWizardStep((s) => Math.min(wizardSteps.length - 1, s + 1));
  }

  // Submit: only valid from Review step (step index 3 = 'review', last = 'submit')
  // Shell calls onSubmit when on isLastStep (Submit step = index 4).
  async function handleWizardSubmit() {
    setPlanActionLoading(true);
    setPlanActionError('');
    try {
      await apiPost(`/api/v1/plans/${planId}/submit`, {});
      setShowSubmitSuccess(true);
    } catch (err) {
      setPlanActionError(getErrorMessage(err, 'Failed to submit plan.'));
    } finally {
      setPlanActionLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // S693 — Wizard mode render
  // Wraps content in PlanWizardShell. Admin mode unchanged below.
  // ---------------------------------------------------------------------------

  if (isWizardMode) {
    // Submit success screen — shown after successful POST /plans/{id}/submit
    if (showSubmitSuccess) {
      return (
        <PlanWizardShell
          steps={wizardSteps}
          currentStep={wizardSteps.length - 1}
          planName={plan.plan_name}
          planCode={plan.plan_code}
          planStatus={planStatus}
        >
          <SubmitSuccessScreen
            plan={plan}
            onReturnToPlans={() => navigate('/admin/plans')}
            onViewStatus={() => {
              setShowSubmitSuccess(false);
              setWizardStep(0);
            }}
          />
        </PlanWizardShell>
      );
    }

    // Derive content for each step
    let stepContent = null;

    if (currentStepKey === 'plan_type') {
      // PATH B Step 0: Plan Type selection
      // Uses PathBEmptyState when no components, or PlanTypeSelector in readOnly when type set
      stepContent = components.length === 0 ? (
        <PathBEmptyState
          plan={plan}
          planId={planId}
          period={period}
          onComplete={() => { load(); setWizardStep(1); }}
        />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-3">
            <p className="text-tok-sm font-medium text-[rgb(var(--color-success))]">Plan type is set.</p>
            <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
              Proceed to Plan Details to configure OTE and weights.
            </p>
          </div>
          <PlanTypeSelector
            planComponentId={selectedComponent?.plan_component_id}
            currentPlanType={selectedComponent?.plan_type_key ?? null}
            readOnly={true}
          />
        </div>
      );
    } else if (currentStepKey === 'source') {
      // PATH A Step 0: Source review (library-deployed, template info)
      stepContent = (
        <div className="flex flex-col gap-4">
          <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] divide-y divide-[rgb(var(--color-border))]">
            <div className="px-4 py-3">
              <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">Deployed From Library</p>
            </div>
            <div className="grid grid-cols-2 gap-x-4 px-4 py-3">
              <div>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Template</p>
                <p className="mt-0.5 text-tok-sm font-medium text-[rgb(var(--color-text))]">
                  {plan.template_name ?? (plan.template_id ? `Template ${plan.template_id}` : '—')}
                </p>
              </div>
              <div>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Plan Name</p>
                <p className="mt-0.5 text-tok-sm font-medium text-[rgb(var(--color-text))]">{plan.plan_name}</p>
              </div>
            </div>
            <div className="px-4 py-3">
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                This plan was deployed from a certified library template. The plan type and module
                composition are pre-configured. Review the source before proceeding to configure details.
              </p>
            </div>
          </div>
        </div>
      );
    } else if (currentStepKey === 'details') {
      // Step 1 (both paths): Plan Details — weights via InlineWeightEditor
      stepContent = (
        <div className="flex flex-col gap-4">
          {selectedComponent ? (
            <InlineWeightEditor
              component={selectedComponent}
              focusField={null}
              onClose={() => {}}
              onSaved={() => load()}
            />
          ) : (
            <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-6 text-center">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No component available to configure.</p>
            </div>
          )}
        </div>
      );
    } else if (currentStepKey === 'configure') {
      // Step 2 (both paths): Configure — Earnings Structure (rate table) then modifier checklist
      // S697: RateTableManager renders above PlanModifierChecklist for ARR-engine plan types.
      //
      // plan_type_key is NOT on component object (confirmed S697 console).
      // core_ic_plan has no modifier_type_code so it never gets an applicability row —
      // weight cards are skipped by set-plan-type. Cannot detect ARR plan from required rows.
      //
      // Detection strategy: show rate table UNLESS a non-ARR primary engine is explicitly
      // present as a required modifier. Non-ARR Tier A modifier_types that would be required:
      //   P3B-TXN (transaction), P4-VOLUME (volume), P4-MARKET (market share),
      //   P4-PHARMA (pharma), P4-POD (team/pod)
      // P3B-OVERLAY and P4-TERRITORY can co-exist with ARR so exclude from blocklist.
      // P4-RETENTION has its own rate table structure — exclude from this manager.
      const _NON_ARR_PRIMARY_MODIFIER_TYPES = new Set([
        'P3B-TXN', 'P4-VOLUME', 'P4-MARKET', 'P4-PHARMA', 'P4-POD',
      ]);
      const _hasNonArrPrimary = applicabilityRows.some(
        (r) => r.applicability === 'required' && _NON_ARR_PRIMARY_MODIFIER_TYPES.has(r.modifier_type),
      );
      // Derive plan type key for RateTableManager planTypeKey prop (used for catalog defaults)
      // Best-effort from required module_code — falls back to arr_attainment when not determinable
      const _REQUIRED_MODULE_TO_PLAN_TYPE = {
        'revenue_type_rules':    'revenue_type_differentiated',
        'territory_compensation':'territory_based',
        'overlay_attribution':   'overlay_specialist',
        'grr_nrr_retention':     'grr_nrr_retention',
        'draw_plan':             'draw_guarantee',
      };
      const _requiredModuleCodes = applicabilityRows
        .filter((r) => r.applicability === 'required')
        .map((r) => r.module_code)
        .filter(Boolean);
      const _derivedPlanTypeKey =
        _requiredModuleCodes.map((mc) => _REQUIRED_MODULE_TO_PLAN_TYPE[mc]).find(Boolean)
        ?? 'arr_attainment';
      const _selectedPlanTypeKey = _hasNonArrPrimary ? null : _derivedPlanTypeKey;
      // Show rate table when component is present, applicability rows loaded, and no non-ARR primary
      const _showRateTable = selectedComponent && applicabilityRows.length > 0 && !_hasNonArrPrimary;

      stepContent = selectedComponent ? (
        <div className="flex flex-col gap-6">
          {/* S697 — Earnings Structure section (S28.13) */}
          {_showRateTable && (
            <section>
              <div className="mb-3">
                <h2 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                  Earnings Structure
                </h2>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                  Define how performance converts to earnings.
                </p>
              </div>
              <RateTableManager
                mode="wizard"
                period={period}
                embedded={true}
                planId={planId ? parseInt(planId, 10) : null}
                planComponentId={selectedComponent.plan_component_id}
                fiscalPeriodId={period?.fiscal_period_id ?? null}
                planTypeKey={_selectedPlanTypeKey}
                roleId={selectedComponent.role_id ?? null}
                segmentId={selectedComponent.segment_id ?? null}
                readOnly={isReadOnly && !hasOpenAmendment}
                onComplete={handleRateTableCompletion}
              />
            </section>
          )}

          {/* S702 — S29.7 Ramp Schedule read-only display (AD-86 secondary surface).
              Shows the most recent effective_date group for this component's role_id.
              Always read-only. No edit controls. Not a submit blocker.
              Configure in Organisation → Ramp Schedules. */}
          {selectedComponent?.role_id && (
            <section>
              <div className="mb-3">
                <h2 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                  Ramp Schedule
                </h2>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                  Attainment factors applied to new hires in this role.
                </p>
              </div>
              <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3">
                {rampScheduleLoading ? (
                  <div className="flex gap-2 animate-pulse">
                    {[1,2,3,4].map((i) => (
                      <div key={i} className="h-6 w-16 rounded-full bg-[rgb(var(--color-surface-muted))]" />
                    ))}
                  </div>
                ) : rampScheduleData.length === 0 ? (
                  <div className="flex items-center gap-2">
                    <svg className="h-4 w-4 shrink-0 text-[rgb(var(--color-text-muted))]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))] italic">
                      No ramp schedule configured for this role.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-tok-xs text-[rgb(var(--color-text-muted))] mr-1">
                        Effective {rampScheduleData[0]?.effective_date ?? ''}
                      </span>
                      {rampScheduleData.map((row) => {
                        const pct = Math.round((Number(row.ramp_factor) ?? 0) * 100);
                        const isFull = pct >= 100;
                        return (
                          <span
                            key={row.ramp_month}
                            className={[
                              'inline-flex items-center rounded-full border px-2.5 py-0.5 text-tok-xs font-medium',
                              isFull
                                ? 'border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 text-[rgb(var(--color-success))]'
                                : 'border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]',
                            ].join(' ')}
                          >
                            M{row.ramp_month} · {isFull ? 'Full' : `${pct}%`}
                          </span>
                        );
                      })}
                    </div>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                      Configured in Organisation → Ramp Schedules.
                    </p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Plan Modifiers section */}
          <section>
            {(_showRateTable) && (
              <div className="mb-3">
                <h2 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                  Plan Modifiers
                </h2>
              </div>
            )}
            {applicabilityRows.length > 0 ? (
              <PlanModifierChecklist
                applicabilityRows={applicabilityRows}
                onCardClick={(modifierType) => {
                  if (WEIGHT_CARD_KEYS.has(modifierType)) return;
                }}
                onExpandedChange={handleModifierExpandedChange}
                onModifierAction={invalidateModifierCache}
                isLocked={isReadOnly && !hasOpenAmendment}
                planId={planId ? parseInt(planId, 10) : null}
                planComponentId={selectedComponent.plan_component_id}
                period={period}
                mode={mode}
                readOnly={isReadOnly && !hasOpenAmendment}
                MANAGER_MAP={MANAGER_MAP}
                MODIFIER_CARDS={MODIFIER_CARDS}
                GRID_KEY_TO_MODIFIER={GRID_KEY_TO_MODIFIER}
              />
            ) : (
              <PlanTypeModifierGrid
                planId={planId ? parseInt(planId, 10) : null}
                roleCategory={roleMap[plan?.role_id]?.role_category ?? null}
                components={components}
                applicabilityRows={applicabilityRows}
                period={period}
                planApproved={plan?.plan_status === 'approved'}
                onCardClick={(modifierType) => {
                  if (WEIGHT_CARD_KEYS.has(modifierType)) return;
                  const translatedKey = GRID_KEY_TO_MODIFIER[modifierType] ?? modifierType;
                  const card = MODIFIER_CARDS.find((c) => c.key === translatedKey);
                  if (card) handleModifierClick(card);
                }}
                onModifierAction={invalidateModifierCache}
                isLocked={isReadOnly && !hasOpenAmendment}
              />
            )}
          </section>
        </div>
      ) : null;
    } else if (currentStepKey === 'review') {
      // Step 3 (both paths): Review — mandatory before submit
      // S694: delta props passed for PATH A plans; ReviewScreen renders TemplateDiffView mode='review'
      stepContent = (
        <ReviewScreen
          plan={plan}
          components={components}
          selectedComponent={selectedComponent}
          applicabilityRows={applicabilityRows}
          roleMap={roleMap}
          segmentMap={segmentMap}
          buMap={buMap}
          planActionError={planActionError}
          rateTableBlocked={rateTableSubmitBlocked}
          deltaData={deltaData}
          deltaLoading={deltaLoading}
          deltaError={deltaError}
          rampScheduleData={rampScheduleData}
        />
      );
    } else if (currentStepKey === 'submit') {
      // Step 4 (both paths): Submit — final confirmation before POST
      // S694: delta props passed here too — AD-74 ReviewScreen reused on Review + Submit steps
      stepContent = (
        <div className="flex flex-col gap-5">
          <ReviewScreen
            plan={plan}
            components={components}
            selectedComponent={selectedComponent}
            applicabilityRows={applicabilityRows}
            roleMap={roleMap}
            segmentMap={segmentMap}
            buMap={buMap}
            planActionError={planActionError}
            rateTableBlocked={rateTableSubmitBlocked}
            deltaData={deltaData}
            deltaLoading={deltaLoading}
            deltaError={deltaError}
            rampScheduleData={rampScheduleData}
          />
          {/* S30.6 — PCMA pre-flight advisory banner.
              Advisory only — UI does not add a new submit blocker here.
              Backend Gate 1 will reject with PCMA_GATE1_FAILED if no rows exist.
              Shown when the selected component has no applicability rows (no plan type set). */}
          {applicabilityRows.length === 0 && (
            <div className="rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/10 px-4 py-3 flex items-start gap-2">
              <svg className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.963-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p className="text-tok-xs text-[rgb(var(--color-warning))]">
                <strong className="font-semibold">No plan type configured for this component.</strong>{' '}
                Submitting will be blocked by the server — configure plan type in the Configure step first.
              </p>
            </div>
          )}

          {!submitBlocked && !rateTableSubmitBlocked && (
            <div className="rounded-xl border border-[rgb(var(--color-primary))]/20 bg-[rgb(var(--color-primary))]/5 px-4 py-3">
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                Clicking <strong className="text-[rgb(var(--color-text))]">Submit for Approval</strong> will
                send this plan to a second Compensation Administrator for review. You will not be able
                to edit the plan until it is reviewed or returned to draft.
              </p>
            </div>
          )}
        </div>
      );
    }

    return (
      <PlanWizardShell
        steps={wizardSteps}
        currentStep={wizardStep}
        onBack={wizardStep > 0 ? handleWizardBack : undefined}
        onNext={handleWizardNext}
        onSubmit={currentStepKey === 'submit' ? handleWizardSubmit : undefined}
        submitDisabled={submitBlocked || rateTableSubmitBlocked || planActionLoading}
        submitLabel={planActionLoading ? 'Submitting…' : 'Submit for Approval'}
        backDisabled={wizardStep === 0}
        nextDisabled={
          (currentStepKey === 'plan_type' && components.length === 0) ||
          (currentStepKey === 'review' && (submitBlocked || rateTableSubmitBlocked))
        }
        progress={currentStepKey === 'configure' ? wizardProgress : null}
        planName={plan.plan_name}
        planCode={plan.plan_code}
        planStatus={planStatus}
      >
        {stepContent}
      </PlanWizardShell>
    );
  }

  return (
    <section className="flex flex-col gap-4">

      {/* Back navigation — only in standalone route mode; workspace provides its own back button */}
      {!planIdProp && (
        <button
          type="button"
          onClick={() => navigate('/admin/plans')}
          className="flex items-center gap-1.5 text-tok-sm text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] w-fit transition-colors"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          All Plans
        </button>
      )}

      {/* ── Plan Header (spec §4.5) ── */}
      <div
        className="flex items-start gap-4 flex-wrap rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-5 py-4"
        style={{ minHeight: '72px' }}
      >
        {/* Left block — plan name + code */}
        <div className="flex-1 min-w-0 max-w-[52%]">
          <h1
            className="text-[18px] leading-[24px] font-semibold text-[rgb(var(--color-text))] truncate"
            title={plan.plan_name}
          >
            {plan.plan_name}
          </h1>
          {plan.plan_code && (
            <p className="mt-0.5 text-[12px] leading-[16px] text-[rgb(var(--color-text-muted))] font-mono">
              {plan.plan_code}
            </p>
          )}
          {planAmendments.length > 0 && (
            <div className="mt-1.5">
              <AmendmentStatusPills
                amendments={planAmendments}
                openAmendmentId={openAmendmentId}
                onClickCommitted={() => {/* future: open amendment detail drawer */}}
              />
            </div>
          )}
        </div>

        {/* Center block — metadata pills */}
        <div className="flex-[0_1_360px] min-w-[240px] flex items-center gap-2 flex-wrap">
          {segmentName && (
            <span className="inline-block rounded-full border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-2.5 py-1 text-tok-xs text-[rgb(var(--color-text-muted))] max-w-[120px] truncate overflow-hidden whitespace-nowrap" title={segmentName}>
              {segmentName}
            </span>
          )}
          {roleName && (
            <span className="inline-block rounded-full border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-2.5 py-1 text-tok-xs text-[rgb(var(--color-text-muted))] max-w-[160px] truncate overflow-hidden whitespace-nowrap" title={roleName}>
              {roleName}
            </span>
          )}
          {buName && (
            <span className="inline-block rounded-full border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-2.5 py-1 text-tok-xs text-[rgb(var(--color-text-muted))] max-w-[180px] truncate overflow-hidden whitespace-nowrap" title={buName}>
              {buName}
            </span>
          )}
        </div>

        {/* Right block — status + actions (§9: [Create Change] [Save as Template]) */}
        <div className="flex-none flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-tok-xs font-medium ${statusStyle}`}>
            {planStatus?.replace(/_/g, ' ')}
          </span>
          {plan.version != null && (
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">v{plan.version}</span>
          )}

          {/* Change 3 — Submit button: visible when plan is draft and has components.
              Blocking count shows how many required modifiers are not yet approved.
              Submit calls POST /api/v1/plans/{id}/submit — sets pending_approval status
              which triggers _derive_plan_status() to re-evaluate (AD-42 compliant). */}
          {planStatus === 'draft' && components.length > 0 && (
            (() => {
              const requiredRows    = applicabilityRows.filter((r) => r.applicability === 'required');
              const unapprovedCount = requiredRows.filter((r) => r.modifier_status !== 'approved').length;
              const blockingLabel   = unapprovedCount > 0
                ? `${unapprovedCount} required modifier${unapprovedCount !== 1 ? 's' : ''} pending`
                : null;
              return (
                <div className="flex flex-col items-end gap-0.5">
                  <button
                    type="button"
                    onClick={handlePlanSubmit}
                    disabled={planActionLoading}
                    className={btnPrimary}
                  >
                    {planActionLoading ? 'Submitting…' : 'Submit for Approval'}
                  </button>
                  {blockingLabel && (
                    <span className="text-tok-xs text-[rgb(var(--color-warning))]">{blockingLabel}</span>
                  )}
                </div>
              );
            })()
          )}

          {/* Actions dropdown — approved, no open amendment (CHANGE-04 / S724) */}
          {isApproved && !hasOpenAmendment && (
            <ActionsDropdown
              plan={plan}
              applicabilityRows={applicabilityRows}
              onAmendPlanEconomics={() => fetchStageImpactAndShowPreview()}
              onAddModifier={handleAddModifier}
              onFixPlanDetails={() => {
                setCorrectionDesc('');
                setCorrectionError('');
                setShowCorrectionModal(true);
              }}
              onUpdateTemplate={() => handleCreateChangeContinue('template')}
              onSaveAsTemplate={handleSaveAsTemplateClick}
            />
          )}

          {/* §9 Save as Template button */}
          {showSaveAsTemplateButton && (
            enableSaveAsTemplateButton ? (
              <button
                type="button"
                onClick={handleSaveAsTemplateClick}
                className={btnSecondary}
              >
                Save as Template
              </button>
            ) : (
              /* Amendment open or no assignments yet → visible but disabled with tooltip */
              <div className="relative group">
                <button
                  type="button"
                  disabled
                  className={btnSecondary + ' cursor-not-allowed'}
                  aria-describedby="sat-disabled-tooltip"
                >
                  Save as Template
                </button>
                <div
                  id="sat-disabled-tooltip"
                  role="tooltip"
                  className="pointer-events-none absolute bottom-full right-0 mb-2 hidden group-hover:block w-56 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))] shadow-lg z-20"
                >
                  {hasOpenAmendment
                    ? 'An amendment is in progress. Complete or cancel it before saving as a template.'
                    : 'Assign this approved plan to at least one position before saving it as a template.'}
                </div>
              </div>
            )
          )}

          {isArchived && (
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))] italic">Read only</span>
          )}
        </div>
      </div>

      {/* Change 10 — Approved plan UX feedback card.
          Shows assignment eligibility status when plan reaches approved state. */}
      {isApproved && !hasOpenAmendment && (
        <div className="flex items-center gap-3 rounded-lg border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-3">
          <svg className="h-4 w-4 shrink-0 text-[rgb(var(--color-success))]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1 min-w-0">
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">Plan Approved — Assignment Eligible</p>
            <p className="text-tok-xs text-[rgb(var(--color-success))]/80">
              All components approved. This plan may be assigned to positions via bulk import or the Assignments tab.
            </p>
          </div>
        </div>
      )}

      {/* ── §7 Governance Summary — visible immediately below header, before scrolling ── */}
      {/* Spec: "Within five seconds the admin must understand: version, template, deployment, acknowledgment" */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-2.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
        {/* Template link */}
        {plan.template_name || plan.template_id ? (
          <span className="flex items-center gap-1.5">
            <span className="font-medium text-[rgb(var(--color-text))]">Template:</span>
            {plan.template_name ?? `Template ${plan.template_id}`}
            {plan.template_version && <span>v{plan.template_version}</span>}
          </span>
        ) : (
          <span className="flex items-center gap-1.5 italic">
            <span className="font-medium not-italic text-[rgb(var(--color-text))]">Template:</span>
            Not linked
          </span>
        )}
        <span className="text-[rgb(var(--color-border))]">·</span>
        {/* Deployment */}
        <span className="flex items-center gap-1.5">
          <span className="font-medium text-[rgb(var(--color-text))]">Deployment:</span>
          {period?.period_label ?? period?.period_code ?? (plan.fiscal_period_id ? `Period ${plan.fiscal_period_id}` : '—')}
          {plan.effective_date && <span>· Effective {fmtDate(plan.effective_date)}</span>}
        </span>
        <span className="text-[rgb(var(--color-border))]">·</span>
        {/* Acknowledgment */}
        <span className="flex items-center gap-1.5">
          <span className="font-medium text-[rgb(var(--color-text))]">Acknowledgment:</span>
          {plan.acknowledged_count != null && plan.assigned_position_count != null
            ? `${plan.acknowledged_count}/${plan.assigned_position_count} reps`
            : plan.acknowledgment_status
              ? String(plan.acknowledgment_status).replace(/_/g, ' ')
              : '—'
          }
        </span>
        {/* Open amendment indicator */}
        {hasOpenAmendment && (
          <>
            <span className="text-[rgb(var(--color-border))]">·</span>
            <span className="flex items-center gap-1 font-medium text-[rgb(var(--color-warning))]">
              <span className="h-1.5 w-1.5 rounded-full bg-[rgb(var(--color-warning))] animate-pulse" />
              Amendment Open
            </span>
          </>
        )}
      </div>

      {/* ── Persistent Amendment Banner — actor-guarded (CHANGE-07 / S724) ── */}
      {/* isAmendmentActor uses initiated_by — confirmed field name from amendment_router.py §11A.1 */}
      {/* AD-178: fail closed — unknown actor = non-actor. Never default to true. */}
      {hasOpenAmendment && (() => {
        const openAmend = planAmendments.find((a) => a.amendment_status === 'open');
        const isAmendmentActor = !!(
          openAmend &&
          openAmend.initiated_by &&
          openAmend.initiated_by === user?.user_account_id
        );
        return (
          <div className="sticky top-0 z-30 rounded-lg border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/10 px-4 py-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-0.5">
                <p className="text-tok-sm font-semibold text-[rgb(var(--color-warning))]">
                  Amendment Open · A-{shortId(openAmendmentId)} · {amendmentReasonLabel}
                </p>
                <p className="text-tok-xs text-[rgb(var(--color-warning))]/80">
                  {isAmendmentActor
                    ? 'Changes to this plan are tracked. Commit or cancel before navigating away.'
                    : 'An amendment is currently in progress. Only the administrator who opened it can commit or cancel it.'}
                </p>
                {/* S729 — Child entity summary: shows what's been added in this amendment.
                    spec_amendment_architecture_v2_0.md §7.4, OI-AMD-07 */}
                {amendmentChildren.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {Object.entries(
                      amendmentChildren.reduce((acc, c) => {
                        const label = c.entity_type.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
                        acc[label] = (acc[label] || 0) + 1;
                        return acc;
                      }, {})
                    ).map(([label, count]) => (
                      <span
                        key={label}
                        className="inline-flex items-center rounded-full border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-warning))]"
                      >
                        {count} {label}{count !== 1 ? 's' : ''} added
                      </span>
                    ))}
                  </div>
                )}
                {cancelError && (
                  <p className="text-tok-xs text-[rgb(var(--color-danger))]">{cancelError}</p>
                )}
              </div>
              {/* Actions only shown to confirmed amendment actor */}
              {isAmendmentActor && (
                <div className="flex items-center gap-2 shrink-0">
                  <button type="button" onClick={handleCommitClick}
                          disabled={committing || cancelling} className={btnWarning}>
                    Commit Amendment
                  </button>
                  <button type="button" onClick={handleCancelAmendment}
                          disabled={cancelling || committing} className={btnDanger}>
                    {cancelling ? 'Cancelling…' : 'Cancel Amendment'}
                  </button>
                </div>
              )}
              {/* CHANGE-13 Part C S737: Add Modifier available to non-actor while amendment open.
                  AD-169: Add Modifier is independent of amendment lifecycle — non-actor CA
                  can still add a new modifier even when another CA has an amendment open.
                  Do NOT show to amendment actor — they already have full actions above. */}
              {!isAmendmentActor && (
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowAddModifierPicker(true)}
                    className={btnSecondary}
                  >
                    Add Modifier
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Post-commit banner (§7A Step 6) ── */}
      {committedAmendmentId && !hasOpenAmendment && (
        <div className="rounded-lg border border-[rgb(var(--color-info))]/30 bg-[rgb(var(--color-info))]/5 px-4 py-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-tok-sm text-[rgb(var(--color-info))]">
              Amendment A-{shortId(committedAmendmentId)} committed.
              {committedStagesCount > 0 && ` ${committedStagesCount} stage${committedStagesCount !== 1 ? 's' : ''} require reapproval.`}
            </p>
            <button type="button" onClick={() => setShowDeltaPanel((v) => !v)} className={btnSecondary}>
              {showDeltaPanel ? 'Hide' : 'View'} Amendment Delta
            </button>
          </div>
          {showDeltaPanel && (
            <div className="mt-3">
              <AmendmentDeltaPanel amendmentId={committedAmendmentId} />
            </div>
          )}
        </div>
      )}

      {/* ── Archived version banner ── */}
      {isArchived && (
        <div className="rounded-lg border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 px-4 py-2 text-tok-sm text-[rgb(var(--color-warning))]">
          Archived Version — Read Only Historical Configuration.
        </div>
      )}

      {/* ── Pending submission banner — Withdraw button active (CHANGE-06 / S725) ── */}
      {/* POST /api/v1/plans/{id}/withdraw-submission implemented S725. Button shown to submitter only. */}
      {planStatus === 'pending_approval' && (
        <div className="flex flex-col gap-2 w-full">
          {/* Withdraw button — submitter only (plan.submitted_by === user.user_account_id) */}
          {plan?.submitted_by && plan.submitted_by === user?.user_account_id && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleWithdrawSubmission}
                disabled={planActionLoading}
                className="rounded-lg border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/5 px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-warning))] hover:bg-[rgb(var(--color-warning))]/10 disabled:opacity-50"
              >
                {planActionLoading ? 'Withdrawing…' : 'Withdraw Submission'}
              </button>
              {planActionError && (
                <span className="text-tok-xs text-[rgb(var(--color-danger))]">{planActionError}</span>
              )}
            </div>
          )}

          {/* CHANGE-13 Part D S737: CA-2 Review & Approve header.
              Shown to non-submitter only — SoD (NN-36): submitter cannot approve own plan.
              "Review & Approve" navigates to full-page review (CHANGE-15 / Step 8).
              Uses URL param reviewComponentId — wired in CPW in Step 8. */}
          {plan?.submitted_by && plan.submitted_by !== user?.user_account_id && (
            <div className="flex items-center justify-between gap-3 rounded-lg border
                            border-[rgb(var(--color-primary))]/30
                            bg-[rgb(var(--color-primary))]/5 px-4 py-3">
              <div className="flex flex-col gap-0.5">
                <p className="text-tok-sm font-semibold text-[rgb(var(--color-primary))]">
                  Pending your approval
                </p>
                <p className="text-tok-xs text-[rgb(var(--color-primary))]/80">
                  This plan has been submitted for CA-2 review. Approve each component to complete.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  // Navigate to full-page review — CHANGE-15 wires PlanComponentReviewPage.
                  // For now: switch to Approvals tab where component queue is visible.
                  const params = new URLSearchParams(window.location.search);
                  params.set('tab', 'approvals');
                  window.location.search = params.toString();
                }}
                className={btnPrimary + ' shrink-0'}
              >
                Review & Approve
              </button>
            </div>
          )}

          <div className="flex items-center gap-3 rounded-lg border
                          border-[rgb(var(--color-info))]/30
                          bg-[rgb(var(--color-info))]/5 px-4 py-3">
            <svg className="h-4 w-4 shrink-0 text-[rgb(var(--color-info))]"
                 fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex flex-col gap-0.5">
              <p className="text-tok-sm font-medium text-[rgb(var(--color-info))]">
                Submitted — awaiting CA-2 approval
              </p>
              {plan?.submitted_by && (
                <p className="text-tok-xs text-[rgb(var(--color-info))]/80">
                  Submitted by {plan.submitted_by_name ?? 'Compensation Administrator'}.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Plan Readiness panel — informational, non-blocking (CHANGE-09 / S724) ── */}
      {/* Confirmed shape from compensation_plans_router.py: overall_status: ready|blocked|warning */}
      {/* checks[].status: pass|fail|warning (NOT ok/blocked as spec guessed). No action_type/action_target. */}
      {/* CA-1 sees Fix → links; CA-2 sees informational only. Do NOT gate Submit on this panel. */}
      {readiness && (
        <div className="rounded-xl border border-[rgb(var(--color-border))]
                        bg-[rgb(var(--color-surface-muted))] px-4 py-3 mb-2">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              Plan Readiness
            </h3>
            {readiness.overall_status === 'ready' && (
              <span className="text-tok-xs text-[rgb(var(--color-success))] font-medium">
                ✓ Ready
              </span>
            )}
            {readiness.overall_status === 'warning' && (
              <span className="text-tok-xs text-[rgb(var(--color-warning))] font-medium">
                ! Warnings
              </span>
            )}
            {readiness.overall_status === 'blocked' && (
              <span className="text-tok-xs text-[rgb(var(--color-danger))] font-medium">
                ✗ Blocked
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            {readiness.checks?.map((item) => (
              <div key={item.check_key}
                   className="flex items-start justify-between gap-2 text-tok-xs">
                <div className="flex items-start gap-1.5">
                  <span className={
                    item.status === 'pass'    ? 'text-[rgb(var(--color-success))] shrink-0 mt-px' :
                    item.status === 'warning' ? 'text-[rgb(var(--color-warning))] shrink-0 mt-px' :
                                                'text-[rgb(var(--color-danger))] shrink-0 mt-px'
                  }>
                    {item.status === 'pass' ? '✓' : item.status === 'warning' ? '!' : '✗'}
                  </span>
                  <span className="text-[rgb(var(--color-text-muted))] leading-relaxed">
                    <span className="font-medium text-[rgb(var(--color-text))]">{item.label}:</span>{' '}
                    {item.detail}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Currency sub-header (spec §4.5) ── */}
      {showCurrencySubheader && (
        <div className="flex items-center gap-4 text-tok-xs text-[rgb(var(--color-text-muted))] px-1">
          <span>
            {oteCurrencies.length > 1
              ? <>OTE Currencies: {oteCurrencies.map(fmtCurrency).join(' · ')}</>
              : <>OTE: {fmtCurrency(oteCurrencies[0])}</>
            }
          </span>
          <span>·</span>
          <span>Reporting: {tenantReportingCurrency}</span>
        </div>
      )}

      {/* ── Component pill strip (spec §4.6) ── */}
      {components.length > 0 && (
        <ComponentPillStrip
          components={components}
          selectedId={selectedComponentId}
          onSelect={handleComponentSelect}
          roleMap={roleMap}
          segmentMap={segmentMap}
        />
      )}

      {/* Change 4 — Custom path (PATH B) empty state.
          PlanTypeSelector requires an existing plan_component_id.
          So: first create a draft component (POST /plan-components), then mount PlanTypeSelector
          with the new component id. On complete, reload so the modifier grid appears.
          Two-step: createComponent state holds the new plan_component_id between steps. */}
      {components.length === 0 && !loading && (
        <PathBEmptyState
          plan={plan}
          planId={planId}
          period={period}
          onComplete={() => load()}
        />
      )}

      {/* CHANGE-12 S737 — PlanConfigSummary: primary view for approved/pending plans (AD-198).
          Renders above the modifier grid, which is demoted to secondary for non-draft states.
          Draft plans: not shown — grid remains the primary configuration surface. */}
      {showPlanConfigSummary && (
        <PlanConfigSummary
          plan={plan}
          components={components}
          selectedComponent={selectedComponent}
          applicabilityRows={applicabilityRows}
          roleMap={roleMap}
          segmentMap={segmentMap}
          buMap={buMap}
          readiness={readiness}
          planAmendments={planAmendments}
        />
      )}

      {/* ── Modifier configuration surface (spec §4.7) ── */}
      {/* S690 OI-PATH-B-UX-SIMPLIFICATION:
          When applicabilityRows are loaded (component has a plan type set),
          show PlanModifierChecklist — type-aware accordion with inline managers.
          When no applicabilityRows (no plan type set yet), show PlanTypeModifierGrid
          — the flat 28-card grid used for PATH A library plans and empty state. */}
      {selectedComponent && (
        <>
          {applicabilityRows.length > 0 && !hasOpenAmendment ? (
            // PlanModifierChecklist: type-aware accordion for new-plan config (PATH B) and
            // approved-plan read-only view. NOT used during active amendments — the full
            // modifier grid must be shown so CA can access all modifier cards while editing.
            <PlanModifierChecklist
              applicabilityRows={applicabilityRows}
              onCardClick={(modifierType) => {
                // CHANGE-14 S737: showToast removed — PlanConfigSummary now renders above
                // the grid and communicates approved-plan state (AD-198). No silent no-op needed.
                // AD-44: weight cards open PlanBuilder inline
                if (WEIGHT_CARD_KEYS.has(modifierType)) {
                  setShowInlinePlanBuilder(true);
                  setInlinePlanBuilderFocusField(WEIGHT_CARD_FOCUS_MAP[modifierType] ?? null);
                  return;
                }
                // AD-45: translate grid key to MODIFIER_CARDS key before lookup
                const translatedKey = GRID_KEY_TO_MODIFIER[modifierType] ?? modifierType;
                const card = MODIFIER_CARDS.find((c) => c.key === translatedKey);
                // Checklist renders manager inline — do NOT call handleModifierClick
                // which mounts the external manager region and causes double-render.
              }}
              onModifierAction={invalidateModifierCache}
              isLocked={isReadOnly && !hasOpenAmendment}
              planId={planId ? parseInt(planId, 10) : null}
              planComponentId={selectedComponent.plan_component_id}
              period={period}
              mode={mode}
              readOnly={isReadOnly && !hasOpenAmendment}
              MANAGER_MAP={MANAGER_MAP}
              MODIFIER_CARDS={MODIFIER_CARDS}
              GRID_KEY_TO_MODIFIER={GRID_KEY_TO_MODIFIER}
            />
          ) : (
            <PlanTypeModifierGrid
              planId={planId ? parseInt(planId, 10) : null}
              roleCategory={roleMap[plan?.role_id]?.role_category ?? null}
              components={components}
              applicabilityRows={applicabilityRows}
              period={period}
              onCardClick={(modifierType) => {
                // CHANGE-14 S737: showToast removed — PlanConfigSummary now renders above
                // the grid and communicates approved-plan state (AD-198). No silent no-op needed.
                // AD-44: weight cards open PlanBuilder inline
                if (WEIGHT_CARD_KEYS.has(modifierType)) {
                  setShowInlinePlanBuilder(true);
                  setInlinePlanBuilderFocusField(WEIGHT_CARD_FOCUS_MAP[modifierType] ?? null);
                  return;
                }
                // AD-45: translate grid key to MODIFIER_CARDS key before lookup
                const translatedKey = GRID_KEY_TO_MODIFIER[modifierType] ?? modifierType;
                const card = MODIFIER_CARDS.find((c) => c.key === translatedKey);
                if (card) handleModifierClick(card);
              }}
              onModifierAction={invalidateModifierCache}
              isLocked={isReadOnly && !hasOpenAmendment}
            />
          )}

          {/* ── AD-44 Inline Weight Editor — weight card clicks (S684) ── */}
          {showInlinePlanBuilder && selectedComponent && (
            <InlineWeightEditor
              component={selectedComponent}
              focusField={inlinePlanBuilderFocusField}
              onClose={() => { setShowInlinePlanBuilder(false); setInlinePlanBuilderFocusField(null); }}
              onSaved={() => { setShowInlinePlanBuilder(false); setInlinePlanBuilderFocusField(null); load(); }}
            />
          )}

                    {/* ── Embedded manager region (spec §4.8) ── */}
          {ActiveManager && selectedComponent && (
            <div className="rounded-xl border border-[rgb(var(--color-primary))]/20 bg-[rgb(var(--color-surface))] overflow-hidden">
              <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-4 py-3 bg-[rgb(var(--color-surface-muted))]">
                <span className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                  {MODIFIER_CARDS.find((c) => c.path === activeModifier)?.label}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setActiveModifier(null);
                    if (!planIdProp) navigate(`/admin/plans/${planId}/components/${selectedComponentId}`, { replace: true });
                  }}
                  className="rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-border))]"
                  title="Close"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-4">
                <ActiveManager
                  mode={mode ?? 'admin'}
                  period={period}
                  wizardSteps={[]}
                  embedded={true}
                  planId={planId ? parseInt(planId, 10) : null}
                  planComponentId={selectedComponent.plan_component_id}
                  fiscalPeriodId={period?.fiscal_period_id ?? null}
                  readOnly={isReadOnly && !hasOpenAmendment}
                  // S729 — Amendment context: modifier managers use these to pass
                  // amendment_id in creation requests on approved plans, and to
                  // gate UI controls appropriately.
                  // spec_amendment_architecture_v2_0.md §7.1
                  amendmentId={openAmendmentId ?? null}
                  planIsApproved={plan?.plan_status === 'approved'}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* ── §10 Package Blueprint — read-only component tree (S549) ── */}
      {/* Renders only when no modifier card is active to preserve screen real estate. */}
      {!activeModifier && planId && (
        <CompensationPackageBlueprint
          planId={planId ? parseInt(planId, 10) : null}
          period={period}
        />
      )}

      {/* ── Version Timeline — §7 vertical chronology by category ── */}
      <VersionTimeline
        plan={plan}
        amendments={planAmendments}
        period={period}
      />

      {/* ── Version Matrix — §7 tabular version history ── */}
      <VersionMatrix
        plan={plan}
        amendments={planAmendments}
        period={period}
      />

      {/* ── SOX Audit View — §7 governance summary with drill-down ── */}
      {/* Minimum required: Version + Approval + Effective Date + Change Type +
          Approver + Reason + Deployment ID + Amendment ID + Ack Status + Artifact */}
      <SoxAuditView
        plan={plan}
        amendments={planAmendments}
        period={period}
        planId={planId}
      />

      {/* ── Toast ── */}
      {toast && (
        <div
          role="status"
          className="fixed bottom-4 right-4 z-50 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text))] shadow-lg"
        >
          {toast}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          Modals
      ══════════════════════════════════════════════════════════════════════ */}

      {/* Add Modifier picker (CHANGE-05 / S724) */}
      <AddModifierPicker
        open={showAddModifierPicker}
        applicabilityRows={applicabilityRows}
        onSelect={handleModifierTypeSelected}
        onCancel={() => setShowAddModifierPicker(false)}
      />

      {/* Stage impact preview — amendment path Step 1 (§7A) */}
      <StageImpactPreview
        open={showStageImpactPreview}
        stageImpactMap={stageImpactMap}
        loading={stageImpactLoading}
        error={stageImpactError}
        onBegin={handleStageImpactBegin}
        onCancel={handleStageImpactCancel}
        assignedPositionCount={assignedPositionCount}
        periodLabel={periodLabel}
      />

      {/* AmendmentBeginModal — wire existing component, DO NOT REBUILD (UI-02 / §7A Step 2) */}
      {showAmendmentModal && selectedComponent && (
        <AmendmentBeginModal
          open={showAmendmentModal}
          entityType="plan_component"
          entityId={String(selectedComponent.plan_component_id)}
          fiscalPeriodId={period?.fiscal_period_id}
          wizardSessionId={wizardSessionId}
          onBegin={(amendmentId, reasonCode, reasonNote) =>
            handleAmendmentBegun(amendmentId, reasonCode, reasonNote)
          }
          onCancel={() => setShowAmendmentModal(false)}
        />
      )}

      {/* Commit confirmation modal (§7A Step 5) */}
      <CommitConfirmModal
        open={showCommitConfirm}
        amendmentId={openAmendmentId}
        reasonCode={amendmentReasonCode}
        reasonNote={amendmentReasonNote}
        stageImpactMap={stageImpactMap}
        onConfirm={handleCommitConfirm}
        onKeepEditing={() => { setShowCommitConfirm(false); setCommitError(''); }}
        committing={committing}
        commitError={commitError}
      />

      {/* Administrative Correction modal (§7A) */}
      {showCorrectionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={(e) => e.stopPropagation()}>
          <div className="w-full max-w-md rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-2xl">
            <div className="border-b border-[rgb(var(--color-border))] px-5 py-4">
              <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Administrative Correction</h2>
              <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-info))]">
                Administrative corrections do not affect payout calculations or require re-acknowledgment.
              </p>
            </div>
            <div className="p-5 flex flex-col gap-4">
              <label className="flex flex-col gap-1 text-tok-sm font-medium text-[rgb(var(--color-text))]">
                Describe the correction (required)
                <textarea
                  value={correctionDesc}
                  onChange={(e) => setCorrectionDesc(e.target.value)}
                  rows={3}
                  placeholder="e.g. Fixed plan code format from AE-FY26-001 to AE.FY26.001"
                  className="mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm placeholder:italic placeholder:text-[rgb(var(--color-placeholder))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]"
                />
              </label>
              {correctionError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                  {correctionError}
                </div>
              )}
            </div>
            <div className="border-t border-[rgb(var(--color-border))] px-5 py-4 flex justify-end gap-2">
              <button type="button" onClick={() => { setShowCorrectionModal(false); setCorrectionError(''); }} className={btnSecondary} disabled={correctionSubmitting}>
                Cancel
              </button>
              <button type="button" onClick={handleCorrectionSubmit} disabled={correctionSubmitting || !correctionDesc.trim()} className={btnPrimary}>
                {correctionSubmitting ? 'Recording…' : 'Record Correction'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* §9 — Save as Template wizard */}
      <SaveAsTemplateWizard
        open={showSaveAsTemplate}
        plan={plan}
        components={components}
        onClose={handleSaveAsTemplateClose}
        onCreated={handleTemplateCreated}
      />

    </section>
  );
}
